import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowDownRight,
  ArrowUpRight,
  Building2,
  CarFront,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Download,
  Filter,
  FlaskConical,
  Fuel,
  HandCoins,
  Image as ImageIcon,
  Landmark,
  Layers3,
  Megaphone,
  Minus,
  PieChart,
  Plus,
  Receipt,
  ShieldCheck,
  ShoppingBag,
  SoapDispenserDroplet,
  Trash2,
  User as UserIcon,
  WalletCards,
  Wrench,
  X,
  XCircle,
  WifiOff,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import useModalBackButton from '../hooks/useModalBackButton';
import useToast from '../hooks/useToast';
import DatePicker from '../components/DatePicker';
import Dropdown from '../components/Dropdown';
import FloatingField from '../components/FloatingField';
import ModalShell from '../components/ModalShell';
import ProtectedImage from '../components/ProtectedImage';
import { API_URL } from '../config';
import { fetchProtectedBlob } from '../utils/protectedMedia';
import { safeFetch } from '../utils/safeFetch';
import { formatPersonName } from '../utils/personName';
import { haptic } from '../utils/vibration';
import { compressImageFile } from '../utils/imageCompression';
import {
  readOfflineRoutesSnapshot,
  OFFLINE_ROUTES_CACHE_UPDATED,
  formatOfflineCacheDateTime,
} from '../utils/offlineRoutesCache';
import {
  formatTransactionFilterBoundary,
  formatTransactionFullMoment,
  formatTransactionMoment,
  parseStoredUtcDate,
  transactionTimestampMs,
} from '../utils/transactionTime';

const EMPLOYEE_SCOPE = 'employee';
const COMPANY_SCOPE = 'company';
const COMPANY_BANK_SCOPE = 'company_bank';
const TRANSACTIONS_PAGE_SIZE = 25;
const AMOUNT_SIGN_ALL = 'all';
const AMOUNT_SIGN_POSITIVE = 'positive';
const AMOUNT_SIGN_NEGATIVE = 'negative';

const generateClientRequestId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
};

const STATUS_META = {
  confirmed: { label: 'Подтверждено', className: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-200', icon: CheckCircle2 },
  pending: { label: 'На подтверждении', className: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-200', icon: Clock3 },
  rejected: { label: 'Отклонено', className: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-200', icon: XCircle },
};

const EMPLOYEE_TAGS = ['Топливо', 'Химия', 'Автомобиль', 'Расходные материалы', 'Обслуживание оборудования', 'Реклама', 'Прочее'];
const COMPANY_TAGS = ['Коммунальные услуги', 'Налоги', 'Заработная плата', 'Закупка химии', 'Новое оборудование', 'Обслуживание оборудования', 'Аренда', 'Реклама', 'Прочее'];
const SYSTEM_TRANSACTION_TAG_MATCHERS = ['сдача выруч', 'приход выруч', 'выручка', 'выдача средств', 'под отчет', 'под отчёт'];

const getTagOptionIcon = (tag) => {
  const normalized = String(tag || '').toLowerCase();

  if (normalized.includes('заработ')) return HandCoins;
  if (normalized.includes('выруч')) return ArrowDownRight;
  if (normalized.includes('аренд')) return Building2;
  if (normalized.includes('коммун')) return SoapDispenserDroplet;
  if (normalized.includes('налог')) return Landmark;
  if (normalized.includes('реклам')) return Megaphone;
  if (normalized.includes('обслуж') && normalized.includes('оборуд')) return Wrench;
  if (normalized.includes('хим')) return FlaskConical;
  if (normalized.includes('топлив')) return Fuel;
  if (normalized.includes('топлив') || normalized.includes('автом') || normalized.includes('проезд')) return CarFront;
  if (normalized.includes('закуп') || normalized.includes('продукт') || normalized.includes('материал')) return ShoppingBag;
  return Receipt;
};

const doesTagMatchAmountSign = (tag, walletScope, amountSign) => {
  if (amountSign === AMOUNT_SIGN_ALL) return true;

  const normalized = String(tag || '').trim().toLowerCase();
  if (!normalized) return true;

  if (amountSign === AMOUNT_SIGN_NEGATIVE) {
    if (walletScope === COMPANY_SCOPE) {
      return !normalized.includes('выруч') && !normalized.includes('оплат');
    }
    return !normalized.includes('выдача средств');
  }

  if (amountSign === AMOUNT_SIGN_POSITIVE) {
    if (walletScope === COMPANY_SCOPE) {
      return normalized.includes('выруч') || normalized.includes('оплат');
    }
    return normalized.includes('выдача средств');
  }

  return true;
};

const isManualExpenseCategoryTag = (tag) => {
  const normalized = String(tag || '').trim().toLowerCase();
  if (!normalized) return false;
  if (normalized === 'расход') return false;
  return !SYSTEM_TRANSACTION_TAG_MATCHERS.some((item) => normalized.includes(item));
};

const buildQuery = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') search.set(key, value);
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

const formatCurrency = (value) => `${Number(value || 0).toFixed(1)} ₽`;
const formatFullCurrency = (value) => `${new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: Number.isInteger(Number(value || 0)) ? 0 : 1,
  maximumFractionDigits: 2,
}).format(Number(value || 0))} ₽`;

const formatInvoiceDeliveryMethod = (value) => {
  if (value === 'paper') return 'Бумажный счёт';
  if (value === 'email') return 'На email';
  return 'Не выбран';
};

const formatAbbreviatedNumber = (value, { withCurrency = false } = {}) => {
  const numeric = Number(value || 0);
  const abs = Math.abs(numeric);
  const suffix = withCurrency ? ' ₽' : '';
  const sign = numeric < 0 ? '-' : '';

  const formatScaled = (scaled, unit) => {
    const rounded = scaled >= 10 ? scaled.toFixed(0) : scaled.toFixed(1);
    return `${sign}${rounded} ${unit}${suffix}`;
  };

  if (abs >= 1_000_000_000_000_000) return formatScaled(abs / 1_000_000_000_000_000, 'квадр');
  if (abs >= 1_000_000_000_000) return formatScaled(abs / 1_000_000_000_000, 'трлн');
  if (abs >= 1_000_000_000) return formatScaled(abs / 1_000_000_000, 'млрд');
  if (abs >= 1_000_000) return formatScaled(abs / 1_000_000, 'млн');
  if (abs >= 10_000) return formatScaled(abs / 1_000, 'тыс');

  return `${numeric.toFixed(1)}${suffix}`;
};

const formatCompactCurrency = (value) => formatAbbreviatedNumber(value, { withCurrency: true });
const formatMetricCurrency = (value) => formatAbbreviatedNumber(value, { withCurrency: false });

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.confirmed;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${meta.className}`}>
      <Icon className="h-3.5 w-3.5" />
      {meta.label}
    </span>
  );
};

const METRIC_TONE_MAP = {
  emerald: 'text-emerald-300',
  red: 'text-rose-300',
  amber: 'text-amber-300',
  blue: 'text-cyan-50',
  violet: 'text-violet-50',
};

const BalanceMetric = ({ icon: Icon, label, value, fullValue, tone = 'emerald' }) => {
  const iconColor = METRIC_TONE_MAP[tone] || METRIC_TONE_MAP.emerald;
  return (
    <div className="rounded-2xl border border-white/20 bg-white/10 px-3 py-3 backdrop-blur-sm">
      <div className="flex flex-col items-start gap-2">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-black/10">
          <Icon className={`h-4 w-4 ${iconColor}`} />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-white/70">{label}</div>
          <div
            className="mt-1 text-[clamp(0.68rem,2.8vw,0.92rem)] font-semibold leading-tight text-white"
            title={fullValue || value}
          >
            {value}
          </div>
        </div>
      </div>
    </div>
  );
};

const TransactionListSkeleton = ({ count = 4, compact = false }) => (
  <div className="divide-y divide-gray-100 dark:divide-gray-800">
    {[...Array(count)].map((_, index) => (
      <div key={index} className={`px-4 ${compact ? 'py-3' : 'py-4'}`}>
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 shrink-0 animate-pulse rounded-2xl bg-gray-200 dark:bg-gray-800" />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="h-4 w-2/5 animate-pulse rounded bg-gray-200 dark:bg-gray-800" />
                <div className="mt-2 h-3 w-1/3 animate-pulse rounded bg-gray-100 dark:bg-gray-800/80" />
              </div>
              <div className="w-24 shrink-0">
                <div className="ml-auto h-4 w-20 animate-pulse rounded bg-gray-200 dark:bg-gray-800" />
                <div className="mt-2 ml-auto h-3 w-16 animate-pulse rounded bg-gray-100 dark:bg-gray-800/80" />
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <div className="h-6 w-24 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
              <div className="h-6 w-20 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const formatSignedCurrency = (value) => {
  const numeric = Number(value || 0);
  const prefix = numeric > 0 ? '+' : '';
  const abs = Math.abs(numeric);
  const formatted = abs >= 10_000 ? formatCompactCurrency(abs) : formatCurrency(abs);
  return `${prefix}${numeric < 0 ? '-' : ''}${formatted}`;
};

const getTransactionPhotoUrl = (transaction) => {
  if (!transaction?.id || !transaction?.photo_path) return '';
  return `${API_URL}/api/transactions/${transaction.id}/photo`;
};

const isSalaryTransaction = (tx) => {
  const type = String(tx?.transaction_type || '').toLowerCase();
  const tag = String(tx?.tag || '').toLowerCase();
  return type.includes('salary') || tag.includes('заработ');
};

const getTransactionTitle = (tx, fallback = 'Операция') => (
  isSalaryTransaction(tx) ? 'Выплата заработной платы' : (tx?.description || tx?.tag || fallback)
);

const getTransactionVisualMeta = (tx) => {
  const tag = String(tx?.tag || '').toLowerCase();
  const type = String(tx?.transaction_type || '').toLowerCase();
  const amount = Number(tx?.amount || 0);

  if (type.includes('salary') || tag.includes('заработ')) {
    return {
      icon: HandCoins,
      wrapClassName: 'bg-violet-50 text-violet-700 dark:bg-violet-950/30 dark:text-violet-200',
      subtitle: 'Зарплата',
    };
  }
  if (type.includes('surrender') || tag.includes('выруч')) {
    return {
      icon: ArrowDownRight,
      wrapClassName: 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-200',
      subtitle: 'Сдача выручки',
    };
  }
  if (type.includes('issue_funds') || tag.includes('под отч') || tag.includes('выдач')) {
    return {
      icon: Plus,
      wrapClassName: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200',
      subtitle: 'Выдача средств',
    };
  }
  if (tag.includes('коммун') || tag.includes('аренд')) {
    return {
      icon: Building2,
      wrapClassName: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950/30 dark:text-cyan-200',
      subtitle: 'Компания',
    };
  }
  if (tag.includes('налог')) {
    return {
      icon: Landmark,
      wrapClassName: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
      subtitle: 'Налоги',
    };
  }
  if (tag.includes('реклам')) {
    return {
      icon: Megaphone,
      wrapClassName: 'bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-200',
      subtitle: 'Реклама',
    };
  }
  if (tag.includes('закуп') || tag.includes('хим') || tag.includes('продукт') || tag.includes('материал')) {
    return {
      icon: ShoppingBag,
      wrapClassName: 'bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-200',
      subtitle: 'Покупки',
    };
  }
  if (tag.includes('автом') || tag.includes('топлив') || tag.includes('проезд')) {
    return {
      icon: Layers3,
      wrapClassName: 'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-200',
      subtitle: 'Транспорт',
    };
  }
  if (type.includes('order_bank_income')) {
    return {
      icon: Landmark,
      wrapClassName: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200',
      subtitle: 'Оплата по счёту',
    };
  }
  if (type.includes('bank_transfer')) {
    return {
      icon: ArrowDownRight,
      wrapClassName: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950/30 dark:text-cyan-200',
      subtitle: 'Перевод между счетами',
    };
  }

  return {
    icon: amount >= 0 ? ArrowUpRight : WalletCards,
    wrapClassName: amount >= 0
      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200'
      : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200',
    subtitle: tx?.wallet_scope === COMPANY_BANK_SCOPE
      ? 'Расчётный счёт'
      : tx?.wallet_scope === COMPANY_SCOPE
        ? 'Компания'
        : 'Сотрудник',
  };
};

const ExpensePhotoViewer = ({ transaction, onClose }) => {
  const viewportRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const gestureStateRef = useRef({
    mode: null,
    startX: 0,
    startY: 0,
    startDistance: 0,
    startScale: 1,
  });
  const [zoomState, setZoomState] = useState({ scale: 1, x: 0, y: 0 });

  useModalBackButton(!!transaction, onClose);

  useEffect(() => {
    setZoomState({ scale: 1, x: 0, y: 0 });
    gestureStateRef.current = {
      mode: null,
      startX: 0,
      startY: 0,
      startDistance: 0,
      startScale: 1,
    };
  }, [transaction?.id, transaction?.photo_path]);

  const getMaxOffset = (scale) => {
    const viewport = viewportRef.current;
    const width = viewport?.clientWidth || window.innerWidth || 0;
    const height = viewport?.clientHeight || window.innerHeight || 0;
    return {
      x: Math.max(0, ((scale - 1) * width) / 2),
      y: Math.max(0, ((scale - 1) * height) / 2),
    };
  };

  const clampZoomState = (nextScale, nextX, nextY) => {
    const scale = Math.min(4, Math.max(1, nextScale));
    if (scale <= 1) {
      return { scale: 1, x: 0, y: 0 };
    }
    const maxOffset = getMaxOffset(scale);
    return {
      scale,
      x: Math.min(maxOffset.x, Math.max(-maxOffset.x, nextX)),
      y: Math.min(maxOffset.y, Math.max(-maxOffset.y, nextY)),
    };
  };

  const getTouchDistance = (touches) => {
    if (!touches || touches.length < 2) return 0;
    const [first, second] = touches;
    return Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY);
  };

  const handleTouchStart = (event) => {
    if (event.touches.length >= 2) {
      gestureStateRef.current = {
        ...gestureStateRef.current,
        mode: 'pinch',
        startDistance: getTouchDistance(event.touches),
        startScale: zoomState.scale,
      };
      return;
    }

    if (event.touches.length === 1 && zoomState.scale > 1) {
      const touch = event.touches[0];
      gestureStateRef.current = {
        ...gestureStateRef.current,
        mode: 'pan',
        startX: touch.clientX - zoomState.x,
        startY: touch.clientY - zoomState.y,
      };
    }
  };

  const handleTouchMove = (event) => {
    if (event.touches.length >= 2) {
      const distance = getTouchDistance(event.touches);
      const baseDistance = gestureStateRef.current.startDistance || distance;
      if (!baseDistance) return;
      event.preventDefault();
      const scale = gestureStateRef.current.startScale * (distance / baseDistance);
      setZoomState((prev) => clampZoomState(scale, prev.x, prev.y));
      return;
    }

    if (gestureStateRef.current.mode === 'pan' && event.touches.length === 1 && zoomState.scale > 1) {
      event.preventDefault();
      const touch = event.touches[0];
      const nextX = touch.clientX - gestureStateRef.current.startX;
      const nextY = touch.clientY - gestureStateRef.current.startY;
      setZoomState((prev) => clampZoomState(prev.scale, nextX, nextY));
    }
  };

  const handleTouchEnd = () => {
    gestureStateRef.current.mode = null;
    setZoomState((prev) => clampZoomState(prev.scale, prev.x, prev.y));
  };

  const handleDoubleClick = () => {
    haptic('selection');
    setZoomState((prev) => (
      prev.scale > 1
        ? { scale: 1, x: 0, y: 0 }
        : { scale: 2, x: 0, y: 0 }
    ));
  };

  const handleDownload = async () => {
    const fileUrl = getTransactionPhotoUrl(transaction);
    if (!fileUrl) return;
    const cleanPath = String(transaction?.photo_path || '').split('?')[0];
    const extension = cleanPath.includes('.') ? cleanPath.split('.').pop() : 'jpg';
    const fileName = `transaction_${transaction?.id || 'photo'}.${extension || 'jpg'}`;
    setDownloading(true);
    try {
      const blob = await fetchProtectedBlob(fileUrl);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      haptic('success');
    } catch (error) {
      console.error('Ошибка скачивания фото расхода:', error);
      haptic('error');
    } finally {
      setDownloading(false);
    }
  };

  const closeWithHaptic = () => {
    haptic('close');
    onClose();
  };

  if (!transaction) return null;

  return createPortal(
    <div className="fixed inset-0 z-[80] flex flex-col bg-black/95 text-white">
      <div className="flex items-center justify-between gap-3 p-3">
        <div className="min-w-0">
          <div className="whitespace-normal break-words text-sm font-medium">{getTransactionTitle(transaction, 'Фото операции')}</div>
          <div className="truncate text-xs text-white/70">{formatTransactionMoment(transaction.timestamp)}</div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-white disabled:opacity-50"
            title="Скачать фото"
            aria-label="Скачать фото"
          >
            <Download className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={closeWithHaptic}
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-white"
            title="Закрыть"
            aria-label="Закрыть"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div
        ref={viewportRef}
        className="flex min-h-0 flex-1 items-center justify-center overflow-hidden p-2 select-none"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        onDoubleClick={handleDoubleClick}
        style={{ touchAction: zoomState.scale > 1 ? 'none' : 'pan-y' }}
      >
        <ProtectedImage
          src={getTransactionPhotoUrl(transaction)}
          showLoader={true}
          loaderText="Загрузка фото..."
          alt="Фото операции"
          className="max-h-full max-w-full object-contain transition-transform duration-150 ease-out"
          style={{
            transform: `translate3d(${zoomState.x}px, ${zoomState.y}px, 0) scale(${zoomState.scale})`,
            transformOrigin: 'center center',
          }}
          draggable={false}
        />
      </div>
    </div>,
    document.body
  );
};

const Expenses = () => {
  const token = localStorage.getItem('token');
  const { toast, showToast, hideToast } = useToast(3200);

  const [walletScope, setWalletScope] = useState(EMPLOYEE_SCOPE);
  const [currentUserId, setCurrentUserId] = useState(0);
  const [financeAccess, setFinanceAccess] = useState({
    is_admin: false,
    can_view_company_wallet: false,
    can_view_analytics: false,
    can_confirm_transactions: false,
    can_delete_transactions: false,
  });
  const [transactions, setTransactions] = useState([]);
  const [pendingCompanyMirrorTransactions, setPendingCompanyMirrorTransactions] = useState([]);
  const [settlementOrders, setSettlementOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [surrenderTargets, setSurrenderTargets] = useState([]);
  const [issueFundsUserId, setIssueFundsUserId] = useState('');
  const [relatedUserId, setRelatedUserId] = useState('');
  const [balance, setBalance] = useState(0);
  const [balanceExpanded, setBalanceExpanded] = useState(false);
  const [summary, setSummary] = useState({ income: 0, expense: 0, count: 0, pending_count: 0, pending_total: 0, by_tag: {}, by_type: {} });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreTransactions, setHasMoreTransactions] = useState(true);
  const [error, setError] = useState('');
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [usingOfflineData, setUsingOfflineData] = useState(false);
  const [offlineSnapshot, setOfflineSnapshot] = useState(null);
  const offlineSnapshotRef = useRef(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showEmployeePickerModal, setShowEmployeePickerModal] = useState(false);
  const [showBankTransferModal, setShowBankTransferModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTx, setActiveTx] = useState(null);
  const [photoViewerTx, setPhotoViewerTx] = useState(null);
  const [isIncome, setIsIncome] = useState(false);
  const [operationKind, setOperationKind] = useState('expense');
  const [companyScenario, setCompanyScenario] = useState('generic');
  const [statusFilter, setStatusFilter] = useState('all');
  const [amountSignFilter, setAmountSignFilter] = useState(AMOUNT_SIGN_ALL);
  const [tagFilter, setTagFilter] = useState('all');
  const [creatorFilter, setCreatorFilter] = useState('');
  const [salaryEmployeeFilter, setSalaryEmployeeFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [tag, setTag] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ creators: [], salary_employees: [] });
  const [bankTransferAmount, setBankTransferAmount] = useState('');
  const [bankTransferDescription, setBankTransferDescription] = useState('');
  const [settlementOrdersLoading, setSettlementOrdersLoading] = useState(false);
  const [settlementActionOrderId, setSettlementActionOrderId] = useState(null);
  const [invoiceDownloadOrderId, setInvoiceDownloadOrderId] = useState(null);
  const [bankTransferLoading, setBankTransferLoading] = useState(false);

  const fileInputRef = useRef(null);
  const loadMoreRef = useRef(null);
  const loadingRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const hasMoreTransactionsRef = useRef(true);
  const transactionsOffsetRef = useRef(0);
  const transactionsRequestTokenRef = useRef(0);
  const createSubmittingRef = useRef(false);
  const createRequestIdRef = useRef('');
  const createRequestFingerprintRef = useRef('');
  const bankTransferLoadingRef = useRef(false);
  const bankTransferRequestIdRef = useRef('');
  const bankTransferRequestFingerprintRef = useRef('');
  const balanceLongPressTimerRef = useRef(null);
  const balanceLongPressHandledRef = useRef(false);
  const closeAddModal = useModalBackButton(showAddModal, () => setShowAddModal(false));
  const closeFilterModal = useModalBackButton(showFilterModal, () => setShowFilterModal(false));
  const closeEmployeePickerModal = useModalBackButton(showEmployeePickerModal, () => setShowEmployeePickerModal(false));
  const closeBankTransferModal = useModalBackButton(showBankTransferModal, () => setShowBankTransferModal(false));
  const closeDeleteConfirm = useModalBackButton(showDeleteConfirm, () => setShowDeleteConfirm(false));
  const closeActiveTx = useModalBackButton(!!activeTx, () => setActiveTx(null));

  const copyTextToClipboard = async (text) => {
    const value = String(text || '').trim();
    if (!value) return false;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = value;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      return true;
    } catch {
      return false;
    }
  };

  const copyBalanceToClipboard = async () => {
    const copied = await copyTextToClipboard(formatFullCurrency(balance));
    if (copied) {
      haptic('success');
      showToast('Баланс скопирован', 'success');
    } else {
      haptic('error');
      showToast('Не удалось скопировать баланс', 'error');
    }
  };

  const startBalanceLongPress = (event) => {
    if (event?.button && event.button !== 0) return;
    if (balanceLongPressTimerRef.current) {
      clearTimeout(balanceLongPressTimerRef.current);
    }
    balanceLongPressHandledRef.current = false;
    balanceLongPressTimerRef.current = window.setTimeout(() => {
      balanceLongPressHandledRef.current = true;
      balanceLongPressTimerRef.current = null;
      copyBalanceToClipboard();
    }, 520);
  };

  const cancelBalanceLongPress = () => {
    if (balanceLongPressTimerRef.current) {
      clearTimeout(balanceLongPressTimerRef.current);
      balanceLongPressTimerRef.current = null;
      return;
    }

    if (balanceLongPressHandledRef.current) {
      window.setTimeout(() => {
        balanceLongPressHandledRef.current = false;
      }, 300);
    }
  };

  const handleBalanceClick = () => {
    if (balanceLongPressHandledRef.current) {
      balanceLongPressHandledRef.current = false;
      return;
    }
    haptic('selection');
    setBalanceExpanded((prev) => !prev);
  };

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => () => {
    if (balanceLongPressTimerRef.current) {
      clearTimeout(balanceLongPressTimerRef.current);
    }
  }, []);

  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  useEffect(() => {
    hasMoreTransactionsRef.current = hasMoreTransactions;
  }, [hasMoreTransactions]);

  useEffect(() => {
    if (!activeTx) {
      setShowDeleteConfirm(false);
    }
  }, [activeTx]);

  const isAdminLike = !!financeAccess.is_admin;
  const canConfirmTransactions = !!financeAccess.can_confirm_transactions;
  const isCompanyWallet = walletScope === COMPANY_SCOPE;
  const isCompanyBankWallet = walletScope === COMPANY_BANK_SCOPE;
  const isCompanyOwnedWallet = isCompanyWallet || isCompanyBankWallet;
  const isSalaryTagFilterActive = isCompanyWallet && String(tagFilter || '').toLowerCase().includes('заработ');
  const selectedEmployeeId = isAdminLike && selectedUserId ? Number(selectedUserId) : currentUserId;
  const resolvedAdminSelectedEmployeeId = isAdminLike
    ? (selectedEmployeeId || currentUserId || 0)
    : 0;
  const targetEmployeeRequestUserId = isAdminLike
    ? (resolvedAdminSelectedEmployeeId || undefined)
    : undefined;
  const selectedEmployee = useMemo(
    () => users.find((user) => Number(user.id) === Number(selectedEmployeeId)) || null,
    [selectedEmployeeId, users]
  );
  const adminUsers = useMemo(() => users.filter((user) => ['admin', 'developer'].includes(user.user_type)), [users]);
  const surrenderTargetUsers = useMemo(
    () => (isAdminLike ? adminUsers : surrenderTargets),
    [adminUsers, isAdminLike, surrenderTargets]
  );
  const manualDynamicTags = useMemo(
    () => tags.filter((item) => isManualExpenseCategoryTag(item)),
    [tags]
  );
  const allAvailableTags = useMemo(() => {
    if (isCompanyBankWallet) return [];
    const staticTags = isCompanyWallet ? COMPANY_TAGS : EMPLOYEE_TAGS;
    return Array.from(new Set([...staticTags, ...manualDynamicTags]));
  }, [isCompanyBankWallet, isCompanyWallet, manualDynamicTags]);
  const availableTags = useMemo(() => {
    if (isCompanyBankWallet) return [];
    const staticTags = (isCompanyWallet ? COMPANY_TAGS : EMPLOYEE_TAGS)
      .filter((item) => doesTagMatchAmountSign(item, walletScope, amountSignFilter));
    const dynamicTags = manualDynamicTags.filter((item) => doesTagMatchAmountSign(item, walletScope, amountSignFilter));
    return Array.from(new Set([...staticTags, ...dynamicTags]));
  }, [amountSignFilter, isCompanyBankWallet, isCompanyWallet, manualDynamicTags, walletScope]);
  const showCategoryFilter = !isCompanyBankWallet;
  const categoryFilterTitle = isCompanyWallet ? 'Категория компании' : 'Категория расхода';
  const creatorFilterTitle = 'Автор операции';
  const creatorFilterPlaceholder = isCompanyBankWallet ? 'Все авторы' : 'Все авторы операций';

  const resetCreateRequestKey = () => {
    createRequestIdRef.current = '';
    createRequestFingerprintRef.current = '';
  };

  const resetBankTransferRequestKey = () => {
    bankTransferRequestIdRef.current = '';
    bankTransferRequestFingerprintRef.current = '';
  };

  const getCreateClientRequestId = (fingerprint) => {
    if (!createRequestIdRef.current || createRequestFingerprintRef.current !== fingerprint) {
      createRequestIdRef.current = generateClientRequestId();
      createRequestFingerprintRef.current = fingerprint;
    }
    return createRequestIdRef.current;
  };

  const getBankTransferClientRequestId = (fingerprint) => {
    if (!bankTransferRequestIdRef.current || bankTransferRequestFingerprintRef.current !== fingerprint) {
      bankTransferRequestIdRef.current = generateClientRequestId();
      bankTransferRequestFingerprintRef.current = fingerprint;
    }
    return bankTransferRequestIdRef.current;
  };

  const withCreateRequestKey = (endpointKey, payload) => ({
    ...payload,
    client_request_id: getCreateClientRequestId(`${endpointKey}:${JSON.stringify(payload)}`),
  });

  const withBankTransferRequestKey = (payload) => ({
    ...payload,
    client_request_id: getBankTransferClientRequestId(`company-bank-transfer:${JSON.stringify(payload)}`),
  });

  const resetForm = () => {
    setAmount('');
    setDescription('');
    setTag('');
    setIssueFundsUserId(isAdminLike ? String(selectedEmployeeId || '') : '');
    setRelatedUserId('');
    setPhotoFile(null);
    setCompanyScenario('generic');
    resetCreateRequestKey();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const loadCachedOfflineExpenses = useCallback(async () => {
    try {
      const rawUserId = localStorage.getItem('user_id');
      const parsedUserId = Number(rawUserId || 0) || null;
      const snapshot = await readOfflineRoutesSnapshot(parsedUserId);
      if (!snapshot || !snapshot.expensesData) {
        return null;
      }
      setOfflineSnapshot(snapshot);
      offlineSnapshotRef.current = snapshot;
      return snapshot.expensesData;
    } catch (err) {
      console.warn('Failed to read offline expenses snapshot', err);
      return null;
    }
  }, []);

  const loadOfflineTransactions = useCallback((scope, targetUserId, reset = true) => {
    const snapshot = offlineSnapshotRef.current;
    const data = snapshot?.expensesData;
    if (!data) return [];

    let sourceTxs = [];
    if (scope === COMPANY_SCOPE) {
      sourceTxs = data.company?.transactions || [];
    } else if (scope === COMPANY_BANK_SCOPE) {
      sourceTxs = data.company_bank?.transactions || [];
    } else {
      const isTargetOther = targetUserId && String(targetUserId) !== String(currentUserId);
      if (isTargetOther && data.employees?.[String(targetUserId)]) {
        sourceTxs = data.employees[String(targetUserId)]?.transactions || [];
      } else {
        sourceTxs = data.my_expenses?.transactions || [];
      }
    }

    const filtered = sourceTxs.filter((tx) => {
      if (statusFilter !== 'all' && tx.status !== statusFilter) return false;
      if (amountSignFilter === AMOUNT_SIGN_POSITIVE && Number(tx.amount || 0) <= 0) return false;
      if (amountSignFilter === AMOUNT_SIGN_NEGATIVE && Number(tx.amount || 0) >= 0) return false;
      if (tagFilter !== 'all' && String(tx.tag || '').toLowerCase() !== String(tagFilter).toLowerCase()) return false;
      if (creatorFilter && String(tx.operator_user_id || tx.user_id || '') !== String(creatorFilter)) return false;
      if (scope === COMPANY_SCOPE && isSalaryTagFilterActive && salaryEmployeeFilter && String(tx.related_user_id || '') !== String(salaryEmployeeFilter)) return false;
      const timestamp = parseStoredUtcDate(tx?.timestamp);
      if (dateFromFilter) {
        const fromDate = parseStoredUtcDate(formatTransactionFilterBoundary(dateFromFilter, false));
        if (!timestamp || timestamp < fromDate) return false;
      }
      if (dateToFilter) {
        const toDate = parseStoredUtcDate(formatTransactionFilterBoundary(dateToFilter, true));
        if (!timestamp || timestamp > toDate) return false;
      }
      return true;
    });

    setTransactions(filtered);
    setHasMoreTransactions(false);
    hasMoreTransactionsRef.current = false;
    setLoading(false);
    setLoadingMore(false);
    return filtered;
  }, [
    amountSignFilter,
    creatorFilter,
    currentUserId,
    dateFromFilter,
    dateToFilter,
    isSalaryTagFilterActive,
    salaryEmployeeFilter,
    statusFilter,
    tagFilter,
  ]);

  const loadOfflineBalanceAndSummary = useCallback((scope, targetUserId) => {
    const snapshot = offlineSnapshotRef.current;
    const data = snapshot?.expensesData;
    if (!data) return;

    let balanceVal = 0;
    let summaryVal = { income: 0, expense: 0, count: 0, pending_count: 0, pending_total: 0, by_tag: {}, by_type: {} };

    if (scope === COMPANY_SCOPE) {
      balanceVal = Number(data.company?.balance || 0);
      summaryVal = data.company?.summary || summaryVal;
    } else if (scope === COMPANY_BANK_SCOPE) {
      balanceVal = Number(data.company_bank?.balance || 0);
      summaryVal = data.company_bank?.summary || summaryVal;
    } else {
      const isTargetOther = targetUserId && String(targetUserId) !== String(currentUserId);
      if (isTargetOther && data.employees?.[String(targetUserId)]) {
        balanceVal = Number(data.employees[String(targetUserId)]?.balance || 0);
        summaryVal = data.employees[String(targetUserId)]?.summary || summaryVal;
      } else {
        balanceVal = Number(data.my_expenses?.balance || 0);
        summaryVal = data.my_expenses?.summary || summaryVal;
      }
    }

    setBalance(balanceVal);
    setSummary(summaryVal);
  }, [currentUserId]);

  const loadOfflineAuxiliary = useCallback((scope) => {
    const snapshot = offlineSnapshotRef.current;
    const data = snapshot?.expensesData;
    if (!data) return;

    if (scope === COMPANY_SCOPE) {
      const pendingList = (data.company?.pending_transactions || []).filter((tx) =>
        String(tx?.transaction_type || '').toLowerCase() === 'revenue_surrender_out'
      ).map((tx) => ({
        ...tx,
        amount: Math.abs(Number(tx.amount || 0)),
        wallet_scope: COMPANY_SCOPE,
        source_transaction_id: tx.id,
        source_transaction: { id: tx.id, wallet_scope: tx.wallet_scope },
        mirror_pending_company_income: true,
        description: tx.description || 'Ожидаемый приход выручки',
        tag: 'Приход выручки',
        related_user_id: tx.user_id,
        related_user_name: tx.user_name,
      }));
      setPendingCompanyMirrorTransactions(pendingList);
      setSettlementOrders([]);
    } else if (scope === COMPANY_BANK_SCOPE) {
      setPendingCompanyMirrorTransactions([]);
      setSettlementOrders(data.company_bank?.settlement_orders || []);
    } else {
      setPendingCompanyMirrorTransactions([]);
      setSettlementOrders([]);
    }
  }, []);

  const applyOfflineData = useCallback(async (customSnapshot = null) => {
    const snapshot = customSnapshot || offlineSnapshotRef.current || await loadCachedOfflineExpenses();
    if (!snapshot || !snapshot.expensesData) {
      return false;
    }
    const data = snapshot.expensesData;
    setUsingOfflineData(true);

    if (data.finance_access) {
      setFinanceAccess(data.finance_access);
    }
    if (Array.isArray(data.users) && data.users.length > 0) {
      setUsers(data.users);
      setSelectedUserId((prev) => {
        if (prev && data.users.some((u) => String(u.id) === String(prev))) return prev;
        return String(data.users[0].id);
      });
    }
    if (Array.isArray(data.tags)) {
      setTags(data.tags);
    }
    if (Array.isArray(data.surrender_targets)) {
      setSurrenderTargets(data.surrender_targets);
    }
    if (data.filter_options) {
      setFilterOptions(data.filter_options);
    }

    return true;
  }, [loadCachedOfflineExpenses]);

  const applyOfflineState = useCallback((scope = walletScope, targetUserId = resolvedAdminSelectedEmployeeId) => {
    loadOfflineBalanceAndSummary(scope, targetUserId);
    loadOfflineTransactions(scope, targetUserId);
    loadOfflineAuxiliary(scope);
  }, [loadOfflineAuxiliary, loadOfflineBalanceAndSummary, loadOfflineTransactions, resolvedAdminSelectedEmployeeId, walletScope]);

  const openBankTransferModal = () => {
    if (usingOfflineData || !navigator.onLine) {
      haptic('warning');
      showToast('Перевод между счетами недоступен в офлайн-режиме', 'warning');
      return;
    }
    haptic('medium');
    setBankTransferAmount('');
    setBankTransferDescription('');
    resetBankTransferRequestKey();
    setShowBankTransferModal(true);
  };

  const openActionModal = (config) => {
    if (usingOfflineData || !navigator.onLine) {
      haptic('warning');
      showToast('Создание операций недоступно в офлайн-режиме', 'warning');
      return;
    }
    haptic('medium');
    resetForm();
    setIsIncome(Boolean(config.isIncome));
    setOperationKind(config.operationKind || 'expense');
    setCompanyScenario(config.companyScenario || 'generic');
    setDescription(config.description || '');
    setTag(config.tag || '');
    setShowAddModal(true);
  };

  const openCreateModal = () => {
    if (usingOfflineData || !navigator.onLine) {
      haptic('warning');
      showToast('Создание операций недоступно в офлайн-режиме', 'warning');
      return;
    }
    if (isCompanyBankWallet) {
      openBankTransferModal();
      return;
    }
    openActionModal({
      isIncome: false,
      operationKind: 'expense',
      companyScenario: 'generic',
      description: '',
      tag: '',
    });
  };

  const fetchUsers = useCallback(async () => {
    if (!isAdminLike) return;
    if (!navigator.onLine || usingOfflineData) {
      const data = offlineSnapshotRef.current?.expensesData;
      if (Array.isArray(data?.users) && data.users.length > 0) {
        setUsers(data.users);
        return;
      }
    }
    try {
      const data = await safeFetch(`${API_URL}/api/users`, { headers: { Authorization: `Bearer ${token}` } });
      const loadedUsers = data.users || [];
      setUsers(loadedUsers);
      setSelectedUserId((prev) => {
        if (prev && loadedUsers.some((user) => String(user.id) === String(prev))) return prev;
        const preferred = loadedUsers.find((user) => Number(user.id) === currentUserId) || loadedUsers[0];
        return preferred ? String(preferred.id) : '';
      });
    } catch {
      const data = offlineSnapshotRef.current?.expensesData;
      if (Array.isArray(data?.users) && data.users.length > 0) {
        setUsers(data.users);
      }
    }
  }, [currentUserId, isAdminLike, token, usingOfflineData]);

  const syncCurrentUserId = useCallback(async () => {
    if (!navigator.onLine) {
      const storedId = Number(localStorage.getItem('user_id') || 0);
      if (storedId) setCurrentUserId(storedId);
      return storedId;
    }
    try {
      const data = await safeFetch(`${API_URL}/api/check-auth`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const resolvedUserId = Number(data?.user_id || data?.id || 0) || 0;
      if (resolvedUserId) {
        setCurrentUserId(resolvedUserId);
        localStorage.setItem('user_id', String(resolvedUserId));
      }
      return resolvedUserId;
    } catch {
      const storedId = Number(localStorage.getItem('user_id') || 0);
      if (storedId) setCurrentUserId(storedId);
      return storedId;
    }
  }, [token]);

  const fetchSurrenderTargets = useCallback(async () => {
    if (!navigator.onLine || usingOfflineData) {
      const data = offlineSnapshotRef.current?.expensesData;
      if (Array.isArray(data?.surrender_targets)) {
        setSurrenderTargets(data.surrender_targets);
        return;
      }
    }
    try {
      const data = await safeFetch(`${API_URL}/api/transactions/surrender-targets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const loadedTargets = Array.isArray(data?.targets) ? data.targets : [];
      setSurrenderTargets(loadedTargets);
      setSelectedAdminId((prev) => (
        prev && loadedTargets.some((user) => String(user.value) === String(prev))
          ? prev
          : (loadedTargets[0]?.value || '')
      ));
    } catch {
      const data = offlineSnapshotRef.current?.expensesData;
      if (Array.isArray(data?.surrender_targets)) {
        setSurrenderTargets(data.surrender_targets);
      } else {
        setSurrenderTargets([]);
      }
    }
  }, [token, usingOfflineData]);

  useEffect(() => {
    if (error) {
      showToast(error, 'error');
    }
  }, [error, showToast]);

  const fetchFinanceAccess = useCallback(async () => {
    if (!navigator.onLine || usingOfflineData) {
      const data = offlineSnapshotRef.current?.expensesData;
      if (data?.finance_access) {
        setFinanceAccess(data.finance_access);
        return;
      }
    }
    try {
      const data = await safeFetch(`${API_URL}/api/transactions/access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFinanceAccess({
        is_admin: !!data?.is_admin,
        can_view_company_wallet: !!data?.can_view_company_wallet,
        can_view_analytics: !!data?.can_view_analytics,
        can_confirm_transactions: !!data?.can_confirm_transactions,
        can_delete_transactions: !!data?.can_delete_transactions,
      });
      if (!data?.can_view_company_wallet) {
        setWalletScope(EMPLOYEE_SCOPE);
      }
    } catch {
      const offlineData = offlineSnapshotRef.current?.expensesData;
      if (offlineData?.finance_access) {
        setFinanceAccess(offlineData.finance_access);
        setUsingOfflineData(true);
        return;
      }
      setFinanceAccess({
        is_admin: false,
        can_view_company_wallet: false,
        can_view_analytics: false,
        can_confirm_transactions: false,
        can_delete_transactions: false,
      });
      setWalletScope(EMPLOYEE_SCOPE);
    }
  }, [token, usingOfflineData]);

  const fetchFilterOptions = useCallback(async () => {
    if (!isAdminLike) {
      setFilterOptions({ creators: [], salary_employees: [] });
      setCreatorFilter('');
      setSalaryEmployeeFilter('');
      return;
    }
    if (!navigator.onLine || usingOfflineData) {
      const data = offlineSnapshotRef.current?.expensesData;
      if (data?.filter_options) {
        setFilterOptions(data.filter_options);
        return;
      }
    }
    try {
      const query = buildQuery({
        wallet_scope: walletScope,
        amount_sign: amountSignFilter !== AMOUNT_SIGN_ALL ? amountSignFilter : undefined,
      });
      const data = await safeFetch(`${API_URL}/api/transactions/filter-options${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const creators = Array.isArray(data?.creators) ? data.creators : [];
      const salaryEmployees = Array.isArray(data?.salary_employees) ? data.salary_employees : [];
      setFilterOptions({ creators, salary_employees: salaryEmployees });
      setCreatorFilter((prev) => (prev && creators.some((item) => String(item.value) === String(prev)) ? prev : ''));
      setSalaryEmployeeFilter((prev) => (prev && salaryEmployees.some((item) => String(item.value) === String(prev)) ? prev : ''));
    } catch {
      const data = offlineSnapshotRef.current?.expensesData;
      if (data?.filter_options) {
        setFilterOptions(data.filter_options);
      } else {
        setFilterOptions({ creators: [], salary_employees: [] });
      }
    }
  }, [amountSignFilter, isAdminLike, token, usingOfflineData, walletScope]);

  const fetchTransactions = useCallback(async ({ reset = false } = {}) => {
    const requestToken = reset ? transactionsRequestTokenRef.current + 1 : transactionsRequestTokenRef.current;
    if (reset) {
      transactionsRequestTokenRef.current = requestToken;
      transactionsOffsetRef.current = 0;
      hasMoreTransactionsRef.current = true;
      setHasMoreTransactions(true);
      setLoading(true);
      setTransactions([]);
    } else {
      if (loadingRef.current || loadingMoreRef.current || !hasMoreTransactionsRef.current) return [];
      setLoadingMore(true);
    }

    if (!navigator.onLine || usingOfflineData) {
      return loadOfflineTransactions(walletScope, resolvedAdminSelectedEmployeeId, reset);
    }

    const offset = reset ? 0 : transactionsOffsetRef.current;
    const qs = buildQuery({
      wallet_scope: walletScope,
      user_id: isCompanyOwnedWallet ? undefined : (isAdminLike ? (resolvedAdminSelectedEmployeeId || undefined) : undefined),
      amount_sign: amountSignFilter !== AMOUNT_SIGN_ALL ? amountSignFilter : undefined,
      operator_user_id: isAdminLike && creatorFilter ? Number(creatorFilter) : undefined,
      related_user_id: isCompanyWallet && isSalaryTagFilterActive && salaryEmployeeFilter ? Number(salaryEmployeeFilter) : undefined,
      status_value: statusFilter !== 'all' ? statusFilter : undefined,
      tag: tagFilter !== 'all' ? tagFilter : undefined,
      date_from: formatTransactionFilterBoundary(dateFromFilter, false),
      date_to: formatTransactionFilterBoundary(dateToFilter, true),
      limit: TRANSACTIONS_PAGE_SIZE,
      offset,
    });

    try {
      const data = await safeFetch(`${API_URL}/api/transactions${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (requestToken !== transactionsRequestTokenRef.current) return [];

      const nextItems = Array.isArray(data) ? data : [];
      transactionsOffsetRef.current = offset + nextItems.length;
      hasMoreTransactionsRef.current = nextItems.length === TRANSACTIONS_PAGE_SIZE;
      setHasMoreTransactions(nextItems.length === TRANSACTIONS_PAGE_SIZE);
      setTransactions((prev) => {
        if (reset) return nextItems;
        const knownIds = new Set(prev.map((item) => item.id));
        return [...prev, ...nextItems.filter((item) => !knownIds.has(item.id))];
      });
      return nextItems;
    } catch (err) {
      if (offlineSnapshotRef.current?.expensesData) {
        applyOfflineData();
        return loadOfflineTransactions(walletScope, resolvedAdminSelectedEmployeeId, reset);
      }
      throw err;
    } finally {
      if (requestToken === transactionsRequestTokenRef.current) {
        if (reset) {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    }
  }, [
    applyOfflineData,
    creatorFilter,
    amountSignFilter,
    dateFromFilter,
    dateToFilter,
    isAdminLike,
    isCompanyOwnedWallet,
    isCompanyWallet,
    isSalaryTagFilterActive,
    loadOfflineTransactions,
    resolvedAdminSelectedEmployeeId,
    salaryEmployeeFilter,
    statusFilter,
    tagFilter,
    token,
    usingOfflineData,
    walletScope,
  ]);

  const fetchPendingCompanyMirrorTransactions = useCallback(async () => {
    if (!isAdminLike || !isCompanyWallet) {
      setPendingCompanyMirrorTransactions([]);
      return;
    }

    if (!navigator.onLine || usingOfflineData) {
      loadOfflineAuxiliary(walletScope);
      return;
    }

    try {
      const data = await safeFetch(`${API_URL}/api/transactions/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pendingItems = Array.isArray(data) ? data : [];
      const filteredItems = pendingItems
        .filter((tx) => String(tx?.transaction_type || '').toLowerCase() === 'revenue_surrender_out')
        .filter((tx) => (amountSignFilter !== AMOUNT_SIGN_NEGATIVE))
        .filter((tx) => {
          if (statusFilter !== 'all' && tx.status !== statusFilter) return false;
          if (tagFilter !== 'all') {
            const normalizedTag = String(tx.tag || '').toLowerCase();
            if (normalizedTag !== String(tagFilter || '').toLowerCase()) return false;
          }
          if (creatorFilter && String(tx.operator_user_id || '') !== String(creatorFilter)) return false;
          const timestamp = parseStoredUtcDate(tx?.timestamp);
          if (dateFromFilter) {
            const fromDate = parseStoredUtcDate(formatTransactionFilterBoundary(dateFromFilter, false));
            if (!timestamp || timestamp < fromDate) return false;
          }
          if (dateToFilter) {
            const toDate = parseStoredUtcDate(formatTransactionFilterBoundary(dateToFilter, true));
            if (!timestamp || timestamp > toDate) return false;
          }
          return true;
        })
        .map((tx) => ({
          ...tx,
          amount: Math.abs(Number(tx.amount || 0)),
          wallet_scope: COMPANY_SCOPE,
          source_transaction_id: tx.id,
          source_transaction: {
            id: tx.id,
            wallet_scope: tx.wallet_scope,
          },
          mirror_pending_company_income: true,
          description: tx.description || 'Ожидаемый приход выручки',
          tag: 'Приход выручки',
          related_user_id: tx.user_id,
          related_user_name: tx.user_name,
        }));

      setPendingCompanyMirrorTransactions(filteredItems);
    } catch {
      if (offlineSnapshotRef.current?.expensesData) {
        loadOfflineAuxiliary(walletScope);
      } else {
        setPendingCompanyMirrorTransactions([]);
      }
    }
  }, [
    creatorFilter,
    amountSignFilter,
    dateFromFilter,
    dateToFilter,
    isAdminLike,
    isCompanyWallet,
    loadOfflineAuxiliary,
    statusFilter,
    tagFilter,
    token,
    usingOfflineData,
    walletScope,
  ]);

  const fetchSettlementOrders = useCallback(async () => {
    if (!isAdminLike || !isCompanyBankWallet) {
      setSettlementOrders([]);
      setSettlementOrdersLoading(false);
      return;
    }

    if (!navigator.onLine || usingOfflineData) {
      loadOfflineAuxiliary(walletScope);
      setSettlementOrdersLoading(false);
      return;
    }

    setSettlementOrdersLoading(true);
    try {
      const data = await safeFetch(`${API_URL}/api/transactions/settlement-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettlementOrders(Array.isArray(data) ? data : []);
    } catch {
      if (offlineSnapshotRef.current?.expensesData) {
        loadOfflineAuxiliary(walletScope);
      } else {
        setSettlementOrders([]);
      }
    } finally {
      setSettlementOrdersLoading(false);
    }
  }, [isAdminLike, isCompanyBankWallet, loadOfflineAuxiliary, token, usingOfflineData, walletScope]);

  const fetchBalance = useCallback(async () => {
    if (!navigator.onLine || usingOfflineData) {
      loadOfflineBalanceAndSummary(walletScope, resolvedAdminSelectedEmployeeId);
      return;
    }
    try {
      const qs = buildQuery({
        wallet_scope: walletScope,
        user_id: isCompanyOwnedWallet ? undefined : (isAdminLike ? (resolvedAdminSelectedEmployeeId || undefined) : undefined),
        amount_sign: amountSignFilter !== AMOUNT_SIGN_ALL ? amountSignFilter : undefined,
      });
      const data = await safeFetch(`${API_URL}/api/transactions/balance${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      setBalance(Number(data.balance || 0));
    } catch (err) {
      if (offlineSnapshotRef.current?.expensesData) {
        applyOfflineData();
        loadOfflineBalanceAndSummary(walletScope, resolvedAdminSelectedEmployeeId);
        return;
      }
      throw err;
    }
  }, [
    amountSignFilter,
    applyOfflineData,
    isAdminLike,
    isCompanyOwnedWallet,
    loadOfflineBalanceAndSummary,
    resolvedAdminSelectedEmployeeId,
    token,
    usingOfflineData,
    walletScope,
  ]);

  const fetchSummary = useCallback(async () => {
    if (!navigator.onLine || usingOfflineData) {
      loadOfflineBalanceAndSummary(walletScope, resolvedAdminSelectedEmployeeId);
      return;
    }
    try {
      const qs = buildQuery({
        wallet_scope: walletScope,
        user_id: isCompanyOwnedWallet ? undefined : (isAdminLike ? (resolvedAdminSelectedEmployeeId || undefined) : undefined),
        amount_sign: amountSignFilter !== AMOUNT_SIGN_ALL ? amountSignFilter : undefined,
      });
      const data = await safeFetch(`${API_URL}/api/transactions/summary${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      setSummary(data || { income: 0, expense: 0, count: 0, pending_count: 0, pending_total: 0, by_tag: {}, by_type: {} });
    } catch (err) {
      if (offlineSnapshotRef.current?.expensesData) {
        applyOfflineData();
        loadOfflineBalanceAndSummary(walletScope, resolvedAdminSelectedEmployeeId);
        return;
      }
      throw err;
    }
  }, [
    amountSignFilter,
    applyOfflineData,
    isAdminLike,
    isCompanyOwnedWallet,
    loadOfflineBalanceAndSummary,
    resolvedAdminSelectedEmployeeId,
    token,
    usingOfflineData,
    walletScope,
  ]);

  const fetchTags = useCallback(async () => {
    if (!navigator.onLine || usingOfflineData) {
      const data = offlineSnapshotRef.current?.expensesData;
      if (Array.isArray(data?.tags)) {
        setTags(data.tags);
        return;
      }
    }
    try {
      const qs = buildQuery({
        wallet_scope: walletScope,
        amount_sign: amountSignFilter !== AMOUNT_SIGN_ALL ? amountSignFilter : undefined,
      });
      const data = await safeFetch(`${API_URL}/api/transactions/tags${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      setTags(data.tags || []);
    } catch {
      const data = offlineSnapshotRef.current?.expensesData;
      if (Array.isArray(data?.tags)) {
        setTags(data.tags);
      }
    }
  }, [amountSignFilter, token, usingOfflineData, walletScope]);

  const refreshHeader = useCallback(async () => {
    try {
      await Promise.all([fetchBalance(), fetchSummary(), fetchTags()]);
    } catch (e) {
      setError(e.message || 'Не удалось загрузить расходы');
    }
  }, [fetchBalance, fetchSummary, fetchTags]);

  const refreshTransactions = useCallback(async () => {
    try {
      await fetchTransactions({ reset: true });
    } catch (e) {
      setError(e.message || 'Не удалось загрузить расходы');
    }
  }, [fetchTransactions]);

  const refreshAll = useCallback(async () => {
    setError('');
    if (!navigator.onLine) {
      const hasData = await applyOfflineData();
      if (hasData) {
        applyOfflineState();
        return;
      }
    }
    await Promise.all([
      refreshTransactions(),
      refreshHeader(),
      isAdminLike ? fetchFilterOptions() : Promise.resolve(),
      fetchPendingCompanyMirrorTransactions(),
      fetchSettlementOrders(),
    ]);
  }, [
    applyOfflineData,
    applyOfflineState,
    fetchFilterOptions,
    fetchPendingCompanyMirrorTransactions,
    fetchSettlementOrders,
    isAdminLike,
    refreshHeader,
    refreshTransactions,
  ]);

  const uploadPhotoIfAny = async (txId) => {
    if (!photoFile || !txId) return;
    const formData = new FormData();
    const preparedPhoto = await compressImageFile(photoFile);
    formData.append('file', preparedPhoto);
    await fetch(`${API_URL}/api/transactions/${txId}/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (usingOfflineData || !navigator.onLine) {
      haptic('warning');
      showToast('Создание операций недоступно в офлайн-режиме', 'warning');
      return;
    }
    if (createSubmittingRef.current) return;

    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount <= 0) {
      haptic('info');
      showToast('Укажите корректную сумму', 'info');
      return;
    }

    createSubmittingRef.current = true;
    setCreateSubmitting(true);
    try {
      let tx = null;
      if (isCompanyWallet) {
        if (companyScenario === 'salary' && !relatedUserId) {
          haptic('info');
          showToast('Для зарплаты выберите сотрудника', 'info');
          return;
        }
        tx = await safeFetch(`${API_URL}/api/transactions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(withCreateRequestKey('transactions', {
            wallet_scope: COMPANY_SCOPE,
            amount: -Math.abs(numericAmount),
            description,
            tag,
            related_user_id: relatedUserId ? Number(relatedUserId) : null,
            transaction_type: companyScenario === 'salary' ? 'salary_payment' : 'company_expense',
          })),
        });
        haptic('success');
        showToast('Расход компании сохранён', 'success');
      } else if (isIncome) {
        const targetIssueUserId = isAdminLike ? Number(issueFundsUserId || 0) : selectedEmployeeId;
        if (!targetIssueUserId) {
          haptic('info');
          showToast('Выберите сотрудника, которому выдать средства', 'info');
          return;
        }
        tx = await safeFetch(`${API_URL}/api/transactions/issue-funds`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(withCreateRequestKey('issue-funds', {
            user_id: targetIssueUserId,
            amount: Math.abs(numericAmount),
            description,
            tag: tag || 'Выдача средств',
          })),
        });
        haptic('success');
        showToast('Средства выданы сотруднику', 'success');
      } else if (operationKind === 'surrender') {
        if (!selectedAdminId) {
          haptic('info');
          showToast('Выберите администратора для подтверждения', 'info');
          return;
        }
        tx = await safeFetch(`${API_URL}/api/transactions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(withCreateRequestKey('transactions', {
            user_id: targetEmployeeRequestUserId,
            amount: -Math.abs(numericAmount),
            description: description || 'Сдача выручки',
            tag: 'Сдача выручки',
            transaction_type: 'revenue_surrender_out',
            target_admin_user_id: Number(selectedAdminId),
          })),
        });
        haptic('success');
        showToast('Операция отправлена на подтверждение', 'success');
      } else {
        tx = await safeFetch(`${API_URL}/api/transactions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(withCreateRequestKey('transactions', {
            user_id: targetEmployeeRequestUserId,
            amount: -Math.abs(numericAmount),
            description,
            tag,
          })),
        });
        haptic(tx?.status === 'pending' ? 'warning' : 'success');
        showToast(tx?.status === 'pending' ? 'Расход отправлен на подтверждение' : 'Расход сохранён', tx?.status === 'pending' ? 'warning' : 'success');
      }

      await uploadPhotoIfAny(tx?.id);
      resetForm();
      setShowAddModal(false);
      await refreshAll();
    } catch (e) {
      haptic('error');
      showToast(e.message || 'Не удалось сохранить операцию', 'error');
    } finally {
      createSubmittingRef.current = false;
      setCreateSubmitting(false);
    }
  };

  const handleApprove = async (id) => {
    if (usingOfflineData || !navigator.onLine) {
      haptic('warning');
      showToast('Подтверждение недоступно в офлайн-режиме', 'warning');
      return;
    }
    try {
      haptic('success');
      await safeFetch(`${API_URL}/api/transactions/${id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      showToast('Операция подтверждена', 'success');
      await refreshAll();
    } catch (e) {
      haptic('error');
      showToast(e.message || 'Не удалось подтвердить операцию', 'error');
    }
  };

  const handleReject = async (id) => {
    if (usingOfflineData || !navigator.onLine) {
      haptic('warning');
      showToast('Отклонение недоступно в офлайн-режиме', 'warning');
      return;
    }
    try {
      haptic('error');
      await safeFetch(`${API_URL}/api/transactions/${id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      showToast('Операция отклонена', 'warning');
      await refreshAll();
    } catch (e) {
      haptic('error');
      showToast(e.message || 'Не удалось отклонить операцию', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (usingOfflineData || !navigator.onLine) {
      haptic('warning');
      showToast('Удаление недоступно в офлайн-режиме', 'warning');
      return;
    }
    try {
      haptic('warning');
      await safeFetch(`${API_URL}/api/transactions/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (activeTx?.id === id) setActiveTx(null);
      showToast('Операция удалена', 'success');
      await refreshAll();
    } catch (e) {
      haptic('error');
      showToast(e.message || 'Не удалось удалить операцию', 'error');
    }
  };

  const handleMarkSettlementPaid = async (orderId) => {
    if (!orderId) return;
    if (usingOfflineData || !navigator.onLine) {
      haptic('warning');
      showToast('Подтверждение оплаты недоступно в офлайн-режиме', 'warning');
      return;
    }
    setSettlementActionOrderId(orderId);
    try {
      await safeFetch(`${API_URL}/api/transactions/settlement-orders/${orderId}/mark-paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      haptic('success');
      showToast('Оплата по счёту подтверждена', 'success');
      await refreshAll();
    } catch (e) {
      haptic('error');
      showToast(e.message || 'Не удалось подтвердить оплату по счёту', 'error');
    } finally {
      setSettlementActionOrderId(null);
    }
  };

  const handleDownloadSettlementInvoice = async (item) => {
    const orderId = item?.order_id;
    const orderNumber = item?.order_number;
    if (!orderId) return;
    if (usingOfflineData || !navigator.onLine) {
      haptic('warning');
      showToast('Скачивание счетов недоступно в офлайн-режиме', 'warning');
      return;
    }
    const packageReady = !!item?.documents_package_ready;
    const url = packageReady
      ? `${API_URL}/api/orders/${orderId}/documents-package-pdf`
      : `${API_URL}/api/orders/${orderId}/invoice-pdf`;
    const safeNumber = String(orderNumber || orderId).replace(/[^a-zA-Z0-9._-]+/g, '_');
    const fileName = packageReady ? `documents_${safeNumber}.pdf` : `invoice_${safeNumber}.pdf`;
    const successMessage = packageReady ? 'Пакет документов скачан' : 'PDF счёта скачан';
    const errorMessage = packageReady ? 'Не удалось скачать пакет документов' : 'Не удалось скачать PDF счёта';

    setInvoiceDownloadOrderId(orderId);
    try {
      const blob = await fetchProtectedBlob(url);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      haptic('success');
      showToast(successMessage, 'success');
      await fetchSettlementOrders();
    } catch (e) {
      haptic('error');
      showToast(e.message || errorMessage, 'error');
    } finally {
      setInvoiceDownloadOrderId(null);
    }
  };

  const handleCompanyBankTransfer = async (event) => {
    event.preventDefault();
    if (usingOfflineData || !navigator.onLine) {
      haptic('warning');
      showToast('Перевод между счетами недоступен в офлайн-режиме', 'warning');
      return;
    }
    if (bankTransferLoadingRef.current) return;

    const numericAmount = Number(bankTransferAmount);
    if (!numericAmount || numericAmount <= 0) {
      haptic('info');
      showToast('Укажите корректную сумму перевода', 'info');
      return;
    }

    bankTransferLoadingRef.current = true;
    setBankTransferLoading(true);
    try {
      const transferPayload = {
        amount: Math.abs(numericAmount),
        description: bankTransferDescription || 'Перевод с расчётного счёта на баланс компании',
        tag: 'Перевод с расчётного счёта',
      };
      await safeFetch(`${API_URL}/api/transactions/company-bank/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(withBankTransferRequestKey(transferPayload)),
      });
      haptic('success');
      showToast('Средства переведены на баланс компании', 'success');
      setBankTransferAmount('');
      setBankTransferDescription('');
      resetBankTransferRequestKey();
      setShowBankTransferModal(false);
      await refreshAll();
    } catch (e) {
      haptic('error');
      showToast(e.message || 'Не удалось перевести средства', 'error');
    } finally {
      bankTransferLoadingRef.current = false;
      setBankTransferLoading(false);
    }
  };

  useEffect(() => {
    loadCachedOfflineExpenses().then((data) => {
      if (!navigator.onLine && data) {
        applyOfflineData().then((hasData) => {
          if (hasData) {
            applyOfflineState();
          }
        });
      }
    }).catch(() => {});
  }, [applyOfflineData, applyOfflineState, loadCachedOfflineExpenses]);

  useEffect(() => {
    const handleOffline = async () => {
      const hasData = await applyOfflineData();
      if (hasData) {
        applyOfflineState();
      }
    };

    const handleOnline = async () => {
      setUsingOfflineData(false);
      refreshAll().catch(() => {});
    };

    const handleCacheUpdated = async () => {
      const data = await loadCachedOfflineExpenses();
      if (usingOfflineData || !navigator.onLine) {
        if (data) {
          await applyOfflineData();
          applyOfflineState();
        }
      }
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    window.addEventListener(OFFLINE_ROUTES_CACHE_UPDATED, handleCacheUpdated);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener(OFFLINE_ROUTES_CACHE_UPDATED, handleCacheUpdated);
    };
  }, [applyOfflineData, applyOfflineState, loadCachedOfflineExpenses, refreshAll, usingOfflineData]);

  useEffect(() => {
    fetchFinanceAccess().catch(() => {});
  }, [fetchFinanceAccess]);

  useEffect(() => {
    syncCurrentUserId().catch(() => {});
  }, [syncCurrentUserId]);

  useEffect(() => {
    if (isAdminLike) fetchUsers().catch(() => {});
  }, [fetchUsers, isAdminLike]);

  useEffect(() => {
    fetchSurrenderTargets().catch(() => {});
  }, [fetchSurrenderTargets]);

  useEffect(() => {
    fetchFilterOptions().catch(() => {});
  }, [fetchFilterOptions]);

  useEffect(() => {
    if (!selectedAdminId && surrenderTargetUsers.length > 0) {
      setSelectedAdminId(String(surrenderTargetUsers[0].value ?? surrenderTargetUsers[0].id));
    }
  }, [selectedAdminId, surrenderTargetUsers]);

  useEffect(() => {
    if (tagFilter !== 'all' && !availableTags.some((item) => String(item) === String(tagFilter))) {
      setTagFilter('all');
    }
  }, [availableTags, tagFilter]);

  useEffect(() => {
    if (!isSalaryTagFilterActive && salaryEmployeeFilter) {
      setSalaryEmployeeFilter('');
    }
  }, [isSalaryTagFilterActive, salaryEmployeeFilter]);

  useEffect(() => {
    if (!showCategoryFilter && tagFilter !== 'all') {
      setTagFilter('all');
    }
  }, [showCategoryFilter, tagFilter]);

  useEffect(() => {
    if (!financeAccess.can_view_company_wallet && walletScope !== EMPLOYEE_SCOPE) setWalletScope(EMPLOYEE_SCOPE);
  }, [financeAccess.can_view_company_wallet, walletScope]);

  useEffect(() => {
    refreshHeader();
  }, [refreshHeader]);

  useEffect(() => {
    refreshTransactions();
  }, [refreshTransactions]);

  useEffect(() => {
    fetchPendingCompanyMirrorTransactions().catch(() => {});
  }, [fetchPendingCompanyMirrorTransactions]);

  useEffect(() => {
    fetchSettlementOrders().catch(() => {});
  }, [fetchSettlementOrders]);

  const loadMoreTransactions = useCallback(() => {
    if (loadingRef.current || loadingMoreRef.current || !hasMoreTransactionsRef.current) return;
    fetchTransactions({ reset: false }).catch((e) => {
      setError(e.message || 'Не удалось загрузить следующую порцию расходов');
    });
  }, [fetchTransactions]);

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel) return undefined;

    const scrollRoot = document.querySelector('.main-scrollable');
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreTransactions();
        }
      },
      { root: scrollRoot || null, rootMargin: '240px 0px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMoreTransactions, transactions.length]);

  const statusFilterOptions = [
    { value: 'all', label: 'Все', icon: Layers3 },
    { value: 'confirmed', label: 'Подтверждённые', icon: CheckCircle2 },
    { value: 'pending', label: 'На подтверждении', icon: Clock3 },
    { value: 'rejected', label: 'Отклонённые', icon: XCircle },
  ];
  const amountSignFilterOptions = [
    { value: AMOUNT_SIGN_ALL, label: 'Все операции', icon: Layers3 },
    { value: AMOUNT_SIGN_POSITIVE, label: 'Только плюс', icon: ArrowUpRight },
    { value: AMOUNT_SIGN_NEGATIVE, label: 'Только минус', icon: ArrowDownRight },
  ];

  const tagFilterOptions = showCategoryFilter
    ? [
      { value: 'all', label: 'Все категории', icon: Layers3 },
      ...availableTags.map((item) => ({ value: item, label: item, icon: getTagOptionIcon(item) })),
    ]
    : [];
  const creatorFilterOptions = [
    { value: '', label: creatorFilterPlaceholder, icon: ShieldCheck },
    ...filterOptions.creators.map((item) => ({ value: String(item.value), label: item.label, icon: ShieldCheck })),
  ];
  const salaryEmployeeFilterOptions = [
    { value: '', label: 'Все сотрудники', icon: HandCoins },
    ...filterOptions.salary_employees.map((item) => ({ value: String(item.value), label: item.label, icon: UserIcon })),
  ];
  const walletScopeOptions = [
    { value: EMPLOYEE_SCOPE, label: 'Сотрудник', icon: UserIcon },
    { value: COMPANY_SCOPE, label: 'Компания', icon: Building2 },
    { value: COMPANY_BANK_SCOPE, label: 'Расчётный счёт', icon: Landmark },
  ];
  const availableWalletScopeOptions = financeAccess.can_view_company_wallet
    ? walletScopeOptions
    : walletScopeOptions.filter((option) => option.value === EMPLOYEE_SCOPE);
  const currentWalletScopeIndex = Math.max(
    0,
    availableWalletScopeOptions.findIndex((option) => option.value === walletScope)
  );
  const currentWalletScopeOption = availableWalletScopeOptions[currentWalletScopeIndex] || walletScopeOptions[0];
  const nextWalletScopeOption = availableWalletScopeOptions[
    (currentWalletScopeIndex + 1) % Math.max(availableWalletScopeOptions.length, 1)
  ] || currentWalletScopeOption;
  const CurrentWalletScopeIcon = currentWalletScopeOption?.icon || UserIcon;

  const pendingSettlementTotal = useMemo(
    () => settlementOrders.reduce((acc, item) => acc + Number(item?.final_amount || 0), 0),
    [settlementOrders]
  );
  const balanceTitle = isCompanyBankWallet
    ? 'Расчётный счёт'
    : isCompanyWallet
      ? 'Баланс компании'
      : isAdminLike
        ? (selectedEmployee ? formatPersonName(selectedEmployee, selectedEmployee.login) : 'Баланс сотрудника')
        : 'Ваш баланс';
  const balanceFullValue = formatFullCurrency(balance);
  const balanceDisplayValue = balanceExpanded ? balanceFullValue : formatCompactCurrency(balance);
  const incomeMetricLabel = isCompanyOwnedWallet ? 'Доходы' : 'Приход';
  const expenseMetricLabel = isCompanyOwnedWallet ? 'Расходы' : 'Расход';
  const pendingMetricLabel = isCompanyBankWallet ? 'Счета' : 'Ожидает';
  const pendingMetricAmount = isCompanyBankWallet
    ? pendingSettlementTotal
    : Math.abs(Number(summary.pending_total || 0));
  const balanceGradientClassName = isCompanyBankWallet
    ? 'from-emerald-500 via-teal-500 to-cyan-600'
    : isCompanyWallet
      ? 'from-violet-500 via-fuchsia-500 to-indigo-600'
      : 'from-blue-500 via-cyan-500 to-indigo-600';
  const hasActiveFilters = statusFilter !== 'all'
    || amountSignFilter !== AMOUNT_SIGN_ALL
    || tagFilter !== 'all'
    || !!creatorFilter
    || !!salaryEmployeeFilter
    || !!dateFromFilter
    || !!dateToFilter;
  const isEmployeeExpenseFlow = !isCompanyOwnedWallet && !isIncome && operationKind === 'expense';
  const isCompanySalaryFlow = isCompanyWallet && companyScenario === 'salary';
  const isGenericCompanyFlow = isCompanyWallet && companyScenario === 'generic';
  const showDescriptionField = isEmployeeExpenseFlow || isGenericCompanyFlow || isCompanySalaryFlow;
  const showCategoryField = isEmployeeExpenseFlow || isGenericCompanyFlow;
  const showPhotoField = isEmployeeExpenseFlow || (isCompanyWallet && !isCompanySalaryFlow);
  const canDeleteActiveTx = !!(activeTx && financeAccess.can_delete_transactions);
  const showActiveTxStatus = !!(activeTx && (activeTx.requires_approval || activeTx.status !== 'confirmed'));
  const activeTxDeleteBlockedReason = activeTx && (
    activeTx.is_system_generated
    || activeTx.source_transaction_id
    || activeTx.source_transaction
    || activeTx.related_transaction_id
  )
    ? 'Системные и связанные операции удалять нельзя'
    : '';
  const numericAmountValue = Number(amount);
  const hasValidAmount = Number.isFinite(numericAmountValue) && numericAmountValue > 0;
  const isCreateFormValid = hasValidAmount
    && (!showCategoryField || !!String(tag || '').trim())
    && (!isCompanySalaryFlow || !!relatedUserId)
    && (!isIncome || !isAdminLike || !!issueFundsUserId)
    && (operationKind !== 'surrender' || !!selectedAdminId);
  const resolveFinanceUserName = useCallback((userId, explicitName) => {
    if (explicitName) return explicitName;
    if (!userId) return null;
    const user = users.find((item) => Number(item.id) === Number(userId));
    return user ? formatPersonName(user, user.login) : null;
  }, [users]);
  const activeTxParticipantRows = useMemo(() => {
    if (!activeTx) return [];

    const rows = [];
    const txType = String(activeTx.transaction_type || '').toLowerCase();
    const ownerName = resolveFinanceUserName(activeTx.user_id, activeTx.user_name);
    const operatorName = resolveFinanceUserName(activeTx.operator_user_id, activeTx.operator_user_name);
    const relatedName = resolveFinanceUserName(activeTx.related_user_id, activeTx.related_user_name);
    const targetAdminName = resolveFinanceUserName(activeTx.target_admin_user_id, activeTx.target_admin_user_name);
    const approverName = resolveFinanceUserName(activeTx.approved_by_user_id, activeTx.approved_by_user_name);
    const rejectorName = resolveFinanceUserName(activeTx.rejected_by_user_id, activeTx.rejected_by_user_name);

    if (txType === 'revenue_surrender_out') {
      if (ownerName) rows.push({ label: 'Передал выручку', value: ownerName });
      if (targetAdminName && activeTx.status === 'pending') rows.push({ label: 'Ожидает подтверждения', value: targetAdminName });
      if (approverName) rows.push({ label: 'Подтвердил', value: approverName });
      if (rejectorName) rows.push({ label: 'Отклонил', value: rejectorName });
      return rows;
    }

    if (txType === 'salary_payment' && relatedName) {
      rows.push({ label: 'Сотрудник', value: relatedName });
    }

    if (operatorName && operatorName !== ownerName) {
      rows.push({ label: 'Создал операцию', value: operatorName });
    } else if (ownerName) {
      rows.push({ label: activeTx.wallet_scope === EMPLOYEE_SCOPE ? 'Сотрудник' : 'Ответственный', value: ownerName });
    }

    if (targetAdminName && activeTx.status === 'pending') rows.push({ label: 'Подтверждает', value: targetAdminName });
    if (approverName) rows.push({ label: 'Подтвердил', value: approverName });
    if (rejectorName) rows.push({ label: 'Отклонил', value: rejectorName });

    return rows;
  }, [activeTx, resolveFinanceUserName]);
  const displayedTransactions = useMemo(() => {
    if (!isCompanyWallet || pendingCompanyMirrorTransactions.length === 0) {
      return transactions;
    }

    const merged = [...pendingCompanyMirrorTransactions, ...transactions];
    const seen = new Set();
    return merged
      .filter((tx) => {
        const mirrorKey = tx.mirror_pending_company_income
          ? `mirror-${tx.id}`
          : `real-${tx.id}`;
        if (seen.has(mirrorKey)) return false;
        seen.add(mirrorKey);
        return true;
      })
      .sort((a, b) => {
        const timeDiff = transactionTimestampMs(b.timestamp) - transactionTimestampMs(a.timestamp);
        if (timeDiff !== 0) return timeDiff;
        return Number(b.id || 0) - Number(a.id || 0);
      });
  }, [isCompanyWallet, pendingCompanyMirrorTransactions, transactions]);
  const addModalTitle = isCompanyWallet
    ? isCompanySalaryFlow ? 'Выплата заработной платы' : 'Новый расход компании'
    : isIncome
      ? 'Выдать средства'
      : operationKind === 'surrender'
        ? 'Сдать выручку'
        : 'Новый расход';
  const addModalHint = isCompanyWallet
    ? isCompanySalaryFlow
      ? 'Укажите сумму, сотрудника и комментарий.'
      : isGenericCompanyFlow
        ? 'Свободный расход компании.'
        : 'Сумма и категория уже выбраны.'
    : isIncome
      ? 'Средства сразу попадут на баланс сотрудника.'
      : operationKind === 'surrender'
        ? 'Операция уйдёт на подтверждение администратору.'
        : '';
  const companyScenarioOptions = [
    { value: 'generic', label: 'Расход', icon: Receipt },
    { value: 'salary', label: 'Зарплата', icon: HandCoins },
    { value: 'utilities', label: 'Коммуналка', icon: SoapDispenserDroplet },
    { value: 'taxes', label: 'Налоги', icon: Landmark },
    { value: 'procurement', label: 'Закупка', icon: ShoppingBag },
  ];
  const employeeOperationOptions = [
    { value: 'expense', label: 'Расход', icon: Receipt },
    { value: 'surrender', label: 'Сдача выручки', icon: ArrowDownRight },
    ...(isAdminLike ? [{ value: 'issue_funds', label: 'Выдать средства', icon: HandCoins }] : []),
  ];
  const transactionTagOptions = allAvailableTags
    .filter((item) => isManualExpenseCategoryTag(item))
    .map((item) => ({ value: item, label: item, icon: getTagOptionIcon(item) }));

  return (
    <div className="space-y-4 pb-24 desktop-content-container-wide">
      {usingOfflineData && (
        <div className="flex items-center gap-2.5 rounded-2xl border border-amber-300/40 bg-amber-50 px-4 py-3 text-xs text-amber-800 shadow-sm dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
          <WifiOff className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="min-w-0 flex-1">
            <span className="font-semibold">Офлайн-режим</span>
            {offlineSnapshot?.syncedAt ? ` · копия от ${formatOfflineCacheDateTime(offlineSnapshot.syncedAt)}` : ''}
            {' · Только просмотр'}
          </div>
        </div>
      )}
      <section className={`relative min-h-[248px] overflow-hidden rounded-2xl bg-gradient-to-br ${balanceGradientClassName} p-4 shadow-sm`}>
        <div className="absolute -right-10 top-8 h-28 w-28 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute bottom-0 right-0 h-20 w-48 rounded-tl-[48px] bg-white/10" />
        <div className="relative flex min-h-[216px] flex-col">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex min-h-[92px] flex-col justify-between">
              {!isCompanyOwnedWallet && isAdminLike ? (
                <button
                  type="button"
                  onClick={() => {
                    haptic('tap');
                    setShowEmployeePickerModal(true);
                  }}
                  className="inline-flex max-w-full items-start gap-1 text-left text-[11px] font-medium uppercase tracking-[0.16em] text-white/80 transition-colors hover:text-white"
                >
                  <span className="line-clamp-2 break-words">{balanceTitle}</span>
                  <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                </button>
              ) : (
                <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-white/70">{balanceTitle}</div>
              )}
              <button
                type="button"
                onClick={handleBalanceClick}
                onMouseDown={startBalanceLongPress}
                onMouseUp={cancelBalanceLongPress}
                onMouseLeave={cancelBalanceLongPress}
                onTouchStart={startBalanceLongPress}
                onTouchEnd={cancelBalanceLongPress}
                onTouchCancel={cancelBalanceLongPress}
                onContextMenu={(event) => event.preventDefault()}
                className={`mt-2 max-w-full select-none rounded-lg bg-transparent p-0 text-left font-semibold text-white transition-colors hover:text-white/90 focus:outline-none focus:ring-0 focus-visible:outline-none ${
                  balanceExpanded
                    ? 'whitespace-normal break-words text-[clamp(1.25rem,6vw,2.1rem)] leading-tight'
                    : 'whitespace-nowrap text-[clamp(1.5rem,7vw,2.55rem)] leading-[0.95]'
                }`}
                title={balanceFullValue}
                aria-label={`Баланс: ${balanceFullValue}`}
              >
                {balanceDisplayValue}
              </button>
            </div>

            <div className="flex min-h-[92px] flex-col items-end justify-between gap-3">
              <div className="flex h-11 items-center gap-2">
                {financeAccess.can_view_company_wallet && currentWalletScopeOption && (
                  <button
                    type="button"
                    onClick={() => {
                      haptic('selection');
                      setWalletScope(nextWalletScopeOption.value);
                    }}
                    className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white transition-colors hover:bg-white/20"
                    title={`Кошелёк: ${currentWalletScopeOption.label}. Следующий: ${nextWalletScopeOption.label}`}
                    aria-label={`Кошелёк: ${currentWalletScopeOption.label}. Переключить на ${nextWalletScopeOption.label}`}
                  >
                    <CurrentWalletScopeIcon className="h-5 w-5" />
                  </button>
                )}
                {financeAccess.can_view_analytics && (
                  <Link
                    to="/expenses/analytics"
                    onClick={() => haptic('tap')}
                    className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white transition-colors hover:bg-white/20"
                  >
                    <PieChart className="h-5 w-5" />
                  </Link>
                )}
              </div>

              <div aria-hidden="true" className="h-9 w-9" />
            </div>
          </div>

          <div className="mt-auto grid grid-cols-3 gap-2">
            <BalanceMetric
              icon={ArrowUpRight}
              label={incomeMetricLabel}
              value={formatMetricCurrency(summary.income)}
              fullValue={formatCurrency(summary.income)}
              tone="emerald"
            />
            <BalanceMetric
              icon={ArrowDownRight}
              label={expenseMetricLabel}
              value={formatMetricCurrency(Math.abs(Number(summary.expense || 0)))}
              fullValue={formatCurrency(Math.abs(Number(summary.expense || 0)))}
              tone="red"
            />
            <BalanceMetric
              icon={Clock3}
              label={pendingMetricLabel}
              value={formatMetricCurrency(pendingMetricAmount)}
              fullValue={formatCurrency(pendingMetricAmount)}
              tone="amber"
            />
          </div>

        </div>
      </section>

      {isCompanyBankWallet && isAdminLike && (
        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="border-b border-gray-100 px-4 py-4 dark:border-gray-800">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-gray-950 dark:text-white">Ожидают оплату</div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Закрытые юрзаказы, по которым деньги ещё не поступили на расчётный счёт.
                </div>
              </div>
              <div className="whitespace-nowrap rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700 dark:bg-amber-950/30 dark:text-amber-200">
                {formatCurrency(pendingSettlementTotal)}
              </div>
            </div>
          </div>

          {settlementOrdersLoading ? (
            <TransactionListSkeleton count={3} compact />
          ) : settlementOrders.length === 0 ? (
            <div className="p-6 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-300">
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <div className="mt-4 text-sm font-medium text-gray-900 dark:text-gray-100">Все оплаты по счёту подтверждены</div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Новые юрзаказы появятся здесь после закрытия.</div>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {settlementOrders.map((item) => {
                const isProcessing = settlementActionOrderId === item.order_id;
                const isDownloadingInvoice = invoiceDownloadOrderId === item.order_id;
                return (
                  <div key={item.order_id} className="px-4 py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Link
                            to={`/order/${item.order_id}`}
                            onClick={() => haptic('tap')}
                            className="text-sm font-semibold text-gray-950 underline-offset-2 hover:underline dark:text-white"
                          >
                            {item.order_number}
                          </Link>
                          <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                            {formatInvoiceDeliveryMethod(item.invoice_delivery_method)}
                          </span>
                          {item.invoice_number ? (
                            <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-medium text-blue-700 dark:bg-blue-950/30 dark:text-blue-200">
                              Счёт № {item.invoice_number}
                            </span>
                          ) : null}
                          {item.invoice_prepared_at ? (
                            <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200">
                              Счёт подготовлен
                            </span>
                          ) : null}
                          {item.act_number ? (
                            <span className="rounded-full bg-violet-50 px-2.5 py-1 text-[11px] font-medium text-violet-700 dark:bg-violet-950/30 dark:text-violet-200">
                              Акт № {item.act_number}
                            </span>
                          ) : null}
                          {item.act_prepared_at ? (
                            <span className="rounded-full bg-cyan-50 px-2.5 py-1 text-[11px] font-medium text-cyan-700 dark:bg-cyan-950/30 dark:text-cyan-200">
                              Акт подготовлен
                            </span>
                          ) : null}
                          {item.documents_package_ready ? (
                            <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-medium text-indigo-700 dark:bg-indigo-950/30 dark:text-indigo-200">
                              Пакет готов
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-1 text-sm text-gray-900 dark:text-gray-100">{item.client_name}</div>
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {item.completed_date
                            ? `Закрыт ${new Date(item.completed_date).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                            : 'Дата закрытия не указана'}
                        </div>
                        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          {item.invoice_prepared_at
                            ? `Счёт сохранён ${new Date(item.invoice_prepared_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                            : 'Счёт ещё не сохранён'}
                        </div>
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {item.act_prepared_at
                            ? `Акт сохранён ${new Date(item.act_prepared_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                            : 'Акт ещё не сохранён'}
                        </div>
                        {!item.documents_package_ready ? (
                          <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                            Перед подтверждением оплаты стоит открыть заказ и сохранить счёт и акт, чтобы собрать полный пакет документов.
                          </div>
                        ) : null}
                      </div>

                      <div className="flex shrink-0 flex-col items-end gap-2">
                        <div className="text-right text-sm font-semibold text-gray-950 dark:text-white">
                          {formatCurrency(item.final_amount)}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDownloadSettlementInvoice(item)}
                          disabled={isDownloadingInvoice}
                          className="inline-flex min-h-[38px] items-center justify-center gap-2 rounded-xl border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                        >
                          <Download className="h-3.5 w-3.5" />
                          {isDownloadingInvoice ? 'Готовим PDF...' : (item.documents_package_ready ? 'PDF пакета' : 'PDF счёта')}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMarkSettlementPaid(item.order_id)}
                          disabled={isProcessing}
                          className="inline-flex min-h-[38px] items-center justify-center rounded-xl bg-emerald-600 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isProcessing ? 'Сохраняем...' : 'Отметить оплату'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="space-y-4 border-b border-gray-100 px-4 py-4 dark:border-gray-800">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-gray-950 dark:text-white">Транзакции</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {tagFilter === 'all' ? 'Все категории' : tagFilter} · показано {displayedTransactions.length}
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  haptic('tap');
                  setShowFilterModal(true);
                }}
                className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border transition-colors ${
                  hasActiveFilters
                    ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-200'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
                }`}
                title="Фильтры"
                aria-label="Фильтры"
              >
                <Filter className="h-4 w-4" />
              </button>
            </div>
          </div>

          {loading ? (
            <TransactionListSkeleton count={4} />
          ) : displayedTransactions.length === 0 ? (
            <div className="p-8 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500">
                <WalletCards className="h-5 w-5" />
              </div>
              <div className="mt-4 text-sm font-medium text-gray-900 dark:text-gray-100">Операций пока нет</div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Попробуйте другой фильтр или создайте первую операцию через кнопку внизу.</div>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {displayedTransactions.map((tx) => {
                const visual = getTransactionVisualMeta(tx);
                const VisualIcon = visual.icon;
                return (
                  <button key={tx.id} type="button" onClick={() => { haptic('tap'); setActiveTx(tx); }} className="w-full px-4 py-4 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${visual.wrapClassName}`}>
                        <VisualIcon className="h-5 w-5" />
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="whitespace-normal break-words text-sm font-semibold leading-snug text-gray-950 dark:text-white">{getTransactionTitle(tx)}</div>
                            {isSalaryTransaction(tx) ? (
                              <>
                                <div className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                                  Кому: {tx.related_user_name || 'Сотрудник не указан'}
                                </div>
                                {tx.description && (
                                  <div className="mt-1 line-clamp-2 break-words text-xs text-gray-600 dark:text-gray-300">
                                    {tx.description}
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                                {visual.subtitle}{tx.tag ? ` · ${tx.tag}` : ''}
                              </div>
                            )}
                          </div>
                          <div className="shrink-0 text-right">
                              <div
                                className={`max-w-[8.5rem] text-right text-sm font-semibold leading-tight ${Number(tx.amount || 0) < 0 ? 'text-red-600' : 'text-emerald-600'}`}
                                title={formatCurrency(tx.amount)}
                              >
                                {formatSignedCurrency(tx.amount)}
                              </div>
                            <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                              {formatTransactionMoment(tx.timestamp)}
                            </div>
                          </div>
                        </div>

                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {tx.status !== 'confirmed' && <StatusBadge status={tx.status} />}
                          {tx.photo_path && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                              <ImageIcon className="h-3.5 w-3.5" />
                              Есть фото
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
              {loadingMore && <TransactionListSkeleton count={2} compact />}
              {!loadingMore && hasMoreTransactions && <div ref={loadMoreRef} className="h-4 w-full" aria-hidden="true" />}
            </div>
          )}
      </section>

      <button
        type="button"
        onClick={() => {
          haptic('medium');
          openCreateModal();
        }}
        className="fixed right-4 z-40 inline-flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-[0_16px_40px_rgba(37,99,235,0.35)] transition-transform hover:scale-105 active:scale-95"
        style={{ bottom: 'calc(max(env(safe-area-inset-bottom, 0px), var(--app-visual-viewport-bottom-inset, 0px)) + 1rem)' }}
      >
        {isCompanyBankWallet ? <ArrowDownRight className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
      </button>

      <ModalShell
        open={showAddModal}
        onClose={() => { haptic('close'); closeAddModal(); }}
        variant="sheet"
        panelClassName="rounded-t-[28px] p-4 sm:max-w-md sm:rounded-[28px]"
      >
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-medium text-gray-950 dark:text-white">{addModalTitle}</div>
                {addModalHint && (
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{addModalHint}</div>
                )}
              </div>
              <button type="button" onClick={() => { haptic('close'); closeAddModal(); }} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><X className="w-4 h-4" /></button>
            </div>

            <form onSubmit={handleCreate} className="space-y-3">
              {isCompanyWallet ? (
                <div className="space-y-2">
                  <Dropdown
                    label="Тип расхода"
                    value={companyScenario}
                    options={companyScenarioOptions}
                    menuLabel="Типы расходов"
                    onChange={(value) => {
                      haptic('selection');
                      setCompanyScenario(value);
                      if (value === 'salary') {
                        setTag('Заработная плата');
                        setDescription('');
                      } else if (value === 'utilities') {
                        setTag('Коммунальные услуги');
                        setDescription('Коммунальные расходы');
                      } else if (value === 'taxes') {
                        setTag('Налоги');
                        setDescription('Налоговые платежи');
                      } else if (value === 'procurement') {
                        setTag('Закупка химии');
                        setDescription('Закупка для работы');
                      } else {
                        setTag('');
                        setDescription('');
                      }
                    }}
                    placeholder="Выберите тип расхода"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Dropdown
                    label="Тип операции"
                    value={isIncome ? 'issue_funds' : operationKind}
                    options={employeeOperationOptions}
                    menuLabel="Типы операций"
                    onChange={(value) => {
                      haptic('selection');
                      if (value === 'issue_funds') {
                        setIsIncome(true);
                        setOperationKind('expense');
                        setDescription('Выдача средств');
                        setTag('Выдача средств');
                        setIssueFundsUserId(String(selectedEmployeeId || ''));
                      } else if (value === 'surrender') {
                        setIsIncome(false);
                        setOperationKind('surrender');
                        setDescription('Сдача выручки');
                        setTag('Сдача выручки');
                        setIssueFundsUserId('');
                      } else {
                        setIsIncome(false);
                        setOperationKind('expense');
                        setDescription('');
                        setTag('');
                        setIssueFundsUserId('');
                      }
                    }}
                    placeholder="Выберите тип операции"
                  />
                </div>
              )}

              {isIncome && isAdminLike && (
                <div className="space-y-2">
                  <Dropdown
                    label="Кому выдать"
                    value={issueFundsUserId}
                    options={users.map((user) => ({
                      value: String(user.id),
                      label: `${formatPersonName(user, user.login)} (@${user.login})`,
                      icon: UserIcon
                    }))}
                    menuLabel="Сотрудники"
                    onChange={(value) => {
                      haptic('selection');
                      setIssueFundsUserId(value);
                    }}
                    placeholder="Выберите сотрудника"
                  />
                </div>
              )}

              <FloatingField label="Сумма">
                <input type="number" step="0.1" min="0" placeholder="Сумма" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full px-3 py-3 border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-sm" />
              </FloatingField>

              {showDescriptionField && !isCompanySalaryFlow && (
                <FloatingField label={isCompanyWallet ? 'Комментарий' : 'Описание'}>
                  <input type="text" placeholder={isCompanyWallet ? 'Комментарий' : 'Описание'} value={description} onChange={(e) => setDescription(e.target.value)} className="w-full px-3 py-3 border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-sm" />
                </FloatingField>
              )}

              {showCategoryField && (
                <div className="space-y-2">
                  <Dropdown
                    label="Категория"
                    value={tag}
                    options={transactionTagOptions}
                    menuLabel="Категории"
                    onChange={(value) => {
                      haptic('selection');
                      setTag(value);
                    }}
                    placeholder="Выберите категорию"
                  />
                </div>
              )}

              {isCompanySalaryFlow && (
                <div className="space-y-2">
                  <Dropdown
                    label="Сотрудник"
                    value={relatedUserId}
                    options={[
                      { value: '', label: 'Выберите сотрудника', icon: HandCoins },
                      ...users.map((user) => ({ value: String(user.id), label: `${formatPersonName(user, user.login)} (@${user.login})`, icon: UserIcon })),
                    ]}
                    menuLabel="Сотрудники"
                    onChange={(value) => {
                      haptic('selection');
                      setRelatedUserId(value);
                    }}
                  />
                </div>
              )}

              {isCompanySalaryFlow && (
                <div className="space-y-2">
                  <FloatingField label="Комментарий">
                    <textarea
                      rows={3}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Комментарий"
                      className="w-full resize-none rounded-xl border border-gray-300 bg-white px-3 py-3 text-sm dark:border-gray-700 dark:bg-gray-800"
                    />
                  </FloatingField>
                </div>
              )}

              {isCompanyWallet && !isGenericCompanyFlow && !isCompanySalaryFlow && (
                <div className="rounded-xl bg-gray-50 px-3 py-3 text-sm text-gray-600 dark:bg-gray-800/80 dark:text-gray-300">
                  {tag || 'Категория выбрана'}
                </div>
              )}

              {!isCompanyWallet && operationKind === 'surrender' && (
                <div className="space-y-2">
                  <Dropdown
                    label="Администратор"
                    value={selectedAdminId}
                    options={surrenderTargetUsers.map((admin) => ({
                      value: String(admin.value ?? admin.id),
                      label: admin.label || `${formatPersonName(admin, admin.login)} (@${admin.login})`,
                      icon: ShieldCheck
                    }))}
                    menuLabel="Администраторы"
                    onChange={(value) => {
                      haptic('selection');
                      setSelectedAdminId(value);
                    }}
                    placeholder="Выберите администратора"
                  />
                </div>
              )}

              {showPhotoField && (
                <>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => { haptic('tap'); fileInputRef.current?.click(); }} className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-800">
                      <ImageIcon className="w-4 h-4" /> {photoFile ? 'Заменить фото' : 'Добавить фото'}
                    </button>
                    {photoFile && <span className="text-xs text-gray-500 truncate max-w-[10rem]">{photoFile.name}</span>}
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} />
                </>
              )}

              <button
                type="submit"
                disabled={!isCreateFormValid || createSubmitting}
                className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-white ${
                  !isCreateFormValid || createSubmitting
                    ? 'cursor-not-allowed bg-gray-300 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    : isIncome
                      ? 'bg-emerald-600 hover:bg-emerald-700'
                      : operationKind === 'surrender'
                        ? 'bg-amber-600 hover:bg-amber-700'
                        : isCompanyWallet
                          ? 'bg-violet-600 hover:bg-violet-700'
                          : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {isIncome ? <Plus className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
                {createSubmitting
                  ? 'Сохраняем...'
                  : isCompanyWallet ? 'Сохранить расход компании' : isIncome ? 'Выдать средства' : operationKind === 'surrender' ? 'Отправить на подтверждение' : 'Сохранить расход'}
              </button>
            </form>
      </ModalShell>

      <ModalShell
        open={showBankTransferModal}
        onClose={() => { haptic('close'); closeBankTransferModal(); }}
        variant="sheet"
        panelClassName="rounded-t-[28px] p-4 sm:max-w-md sm:rounded-[28px]"
      >
            <div className="mb-2 flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-950 dark:text-white">Перевод на баланс компании</div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Средства спишутся с расчётного счёта и поступят в основной баланс компании.
                </div>
              </div>
              <button type="button" onClick={() => { haptic('close'); closeBankTransferModal(); }} className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800">
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleCompanyBankTransfer} className="space-y-3">
              <FloatingField label="Сумма перевода">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={bankTransferAmount}
                  onChange={(event) => setBankTransferAmount(event.target.value)}
                  placeholder="Сумма перевода"
                  className="w-full rounded-xl border border-gray-300 bg-white px-3 py-3 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                />
              </FloatingField>
              <FloatingField label="Комментарий">
                <input
                  type="text"
                  value={bankTransferDescription}
                  onChange={(event) => setBankTransferDescription(event.target.value)}
                  placeholder="Комментарий"
                  className="w-full rounded-xl border border-gray-300 bg-white px-3 py-3 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                />
              </FloatingField>
              <button
                type="submit"
                disabled={bankTransferLoading || !(Number(bankTransferAmount) > 0)}
                className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-medium text-white ${
                  bankTransferLoading || !(Number(bankTransferAmount) > 0)
                    ? 'cursor-not-allowed bg-gray-300 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
              >
                <ArrowDownRight className="h-4 w-4" />
                {bankTransferLoading ? 'Переводим...' : 'Перевести на баланс'}
              </button>
            </form>
      </ModalShell>

      {activeTx && !photoViewerTx && (
        <ModalShell
          open
          onClose={() => { haptic('close'); closeActiveTx(); }}
          variant="sheet"
          panelClassName="max-h-[88dvh] overflow-y-auto p-4 sm:max-w-md"
        >
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium">{isSalaryTransaction(activeTx) ? 'Выплата заработной платы' : 'Операция'}</div>
              <button type="button" onClick={() => { haptic('close'); closeActiveTx(); }} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Сумма</span><span className={`font-semibold ${activeTx.amount < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{formatCurrency(activeTx.amount)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Кошелёк</span><span>{activeTx.wallet_scope === COMPANY_BANK_SCOPE ? 'Расчётный счёт' : activeTx.wallet_scope === COMPANY_SCOPE ? 'Компания' : 'Сотрудник'}</span></div>
              {showActiveTxStatus && (
                <div className="flex justify-between items-center gap-3"><span className="text-gray-500">Статус</span><StatusBadge status={activeTx.status} /></div>
              )}
              <div className="flex justify-between"><span className="text-gray-500">Тег</span><span>{activeTx.tag || '—'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Дата</span><span>{formatTransactionFullMoment(activeTx.timestamp)}</span></div>
              {activeTxParticipantRows.length > 0 && (
                <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3 dark:border-gray-800 dark:bg-gray-950/40">
                  <div className="mb-2 text-xs font-medium uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">Участники</div>
                  <div className="space-y-2">
                    {activeTxParticipantRows.map((row) => (
                      <div key={`${row.label}-${row.value}`} className="flex items-start justify-between gap-3">
                        <span className="text-gray-500">{row.label}</span>
                        <span className="text-right text-gray-900 dark:text-gray-100">{row.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div><div className="text-gray-500 mb-1">{isSalaryTransaction(activeTx) ? 'Комментарий' : 'Описание'}</div><div className="text-gray-900 dark:text-gray-100">{activeTx.description || '—'}</div></div>
              {activeTx.rejection_reason && <div><div className="text-gray-500 mb-1">Причина отклонения</div><div className="text-red-600">{activeTx.rejection_reason}</div></div>}
              {activeTx.photo_path && (
                <div>
                  <div className="mb-1 text-gray-500">Фото</div>
                  <button
                    type="button"
                    onClick={() => {
                      haptic('open');
                      setPhotoViewerTx(activeTx);
                    }}
                    className="block"
                  >
                    <ProtectedImage
                      src={getTransactionPhotoUrl(activeTx)}
                      alt="Фото"
                      className="h-28 w-28 rounded-xl border border-gray-200 object-cover dark:border-gray-800"
                    />
                  </button>
                </div>
              )}
            </div>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
              {canConfirmTransactions && activeTx.status === 'pending' && (
                <>
                  <button
                    type="button"
                    onClick={async () => {
                      await handleReject(activeTx.id);
                      setActiveTx(null);
                    }}
                    className="inline-flex min-h-[42px] w-full items-center justify-center gap-2 rounded-xl border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950/40"
                  >
                    <XCircle className="w-4 h-4" /> Отклонить
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      await handleApprove(activeTx.id);
                      setActiveTx(null);
                    }}
                    className="inline-flex min-h-[42px] w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700"
                  >
                    <CheckCircle2 className="w-4 h-4" /> Подтвердить
                  </button>
                </>
              )}
              {canDeleteActiveTx && (
                <button
                  type="button"
                  onClick={() => {
                    haptic('warning');
                    setShowDeleteConfirm(true);
                  }}
                  disabled={!!activeTxDeleteBlockedReason}
                  title={activeTxDeleteBlockedReason || 'Удалить операцию'}
                  className="inline-flex min-h-[42px] w-full items-center justify-center gap-2 rounded-xl border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-900 dark:hover:bg-red-950/40"
                >
                  <Trash2 className="w-4 h-4" /> Удалить
                </button>
              )}
            </div>
        </ModalShell>
      )}
      <ModalShell
        open={showFilterModal}
        onClose={() => { haptic('close'); closeFilterModal(); }}
        variant="sheet"
        panelClassName="max-h-[88dvh] overflow-visible rounded-t-[28px] p-4 sm:max-w-md sm:rounded-[28px] sm:pb-4"
        panelProps={{ style: { paddingBottom: 'calc(max(env(safe-area-inset-bottom, 0px), var(--app-visual-viewport-bottom-inset, 0px)) + 1rem)' } }}
      >
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-medium text-gray-950 dark:text-white">Фильтры</div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Отберите нужные операции в ленте.</div>
              </div>
              <button type="button" onClick={() => { haptic('close'); closeFilterModal(); }} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><X className="w-4 h-4" /></button>
            </div>

            <div className="space-y-4">
                <div>
                  <Dropdown
                    label="Тип суммы"
                    value={amountSignFilter}
                    options={amountSignFilterOptions}
                    onChange={(value) => {
                      haptic('selection');
                      setAmountSignFilter(value);
                    }}
                  />
                </div>

                {showCategoryFilter && (
                  <div>
                    <Dropdown
                      label={categoryFilterTitle}
                      value={tagFilter}
                      options={tagFilterOptions}
                      onChange={(value) => {
                        haptic('selection');
                        setTagFilter(value);
                      }}
                    />
                  </div>
                )}

              {isAdminLike && (
                <div>
                  <Dropdown
                    label={creatorFilterTitle}
                    value={creatorFilter}
                    options={creatorFilterOptions}
                    onChange={(value) => {
                      haptic('selection');
                      setCreatorFilter(value);
                    }}
                  />
                </div>
              )}

              {isAdminLike && isSalaryTagFilterActive && (
                <div>
                  <Dropdown
                    label="Сотрудник"
                    value={salaryEmployeeFilter}
                    options={salaryEmployeeFilterOptions}
                    onChange={(value) => {
                      haptic('selection');
                      setSalaryEmployeeFilter(value);
                    }}
                  />
                </div>
              )}

              <div>
                <Dropdown
                  label="Статус"
                  value={statusFilter}
                  options={statusFilterOptions}
                  onChange={(value) => {
                    haptic('selection');
                    setStatusFilter(value);
                  }}
                />
              </div>

              <div>
                <div className="mb-2 text-xs font-medium uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">Период</div>
                <div className="grid grid-cols-2 gap-3">
                  <DatePicker
                    value={dateFromFilter}
                    onChange={setDateFromFilter}
                    placeholder="От"
                    allowPastDates
                    showTodayButton={false}
                    inputClassName="!rounded-xl !py-3 !pl-8 !pr-8 !text-left !text-sm !placeholder:text-sm"
                  />
                  <DatePicker
                    value={dateToFilter}
                    onChange={setDateToFilter}
                    placeholder="До"
                    allowPastDates
                    showTodayButton={false}
                    inputClassName="!rounded-xl !py-3 !pl-8 !pr-8 !text-left !text-sm !placeholder:text-sm"
                  />
                </div>
              </div>

              <div className="-mx-4 mt-1 border-t border-gray-100 bg-white px-4 pt-3 dark:border-gray-800 dark:bg-gray-900 sm:mx-0 sm:border-t-0 sm:bg-transparent sm:px-0 sm:pt-0">
                <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                    onClick={() => {
                      haptic('selection');
                      setStatusFilter('all');
                      setAmountSignFilter(AMOUNT_SIGN_ALL);
                      setTagFilter('all');
                      setCreatorFilter('');
                      setSalaryEmployeeFilter('');
                      setDateFromFilter('');
                      setDateToFilter('');
                    }}
                  className="rounded-xl border border-gray-200 px-3 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  Сбросить
                </button>
                <button
                  type="button"
                  onClick={() => {
                    haptic('success');
                    closeFilterModal();
                  }}
                  className="rounded-xl bg-blue-600 px-3 py-3 text-sm font-medium text-white hover:bg-blue-700"
                >
                  Готово
                </button>
                </div>
              </div>
            </div>
      </ModalShell>
      <ModalShell
        open={showEmployeePickerModal}
        onClose={() => { haptic('close'); closeEmployeePickerModal(); }}
        variant="sheet"
        panelClassName="rounded-t-[28px] p-4 sm:max-w-md sm:rounded-[28px]"
      >
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-medium text-gray-950 dark:text-white">Выбор сотрудника</div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Выберите, чей баланс и операции смотреть.</div>
              </div>
              <button type="button" onClick={() => { haptic('close'); closeEmployeePickerModal(); }} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><X className="w-4 h-4" /></button>
            </div>

            <Dropdown
              label="Сотрудник"
              value={selectedUserId}
              options={users.map((user) => ({
                value: String(user.id),
                label: `${formatPersonName(user, user.login)} (@${user.login})`,
                icon: UserIcon,
              }))}
              onChange={(value) => {
                haptic('selection');
                setSelectedUserId(value);
                closeEmployeePickerModal();
              }}
              placeholder="Выберите сотрудника"
            />
      </ModalShell>
      {showDeleteConfirm && activeTx && !photoViewerTx && (
        <ModalShell
          open
          onClose={() => { haptic('close'); closeDeleteConfirm(); }}
          variant="sheet"
          zIndexClass="z-[60]"
          containerClassName="!bg-black/55"
          panelClassName="rounded-t-[28px] p-4 sm:max-w-md sm:rounded-[28px]"
        >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-medium text-gray-950 dark:text-white">Удалить операцию?</div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {activeTx.status === 'confirmed'
                    ? 'Подтверждённая операция будет удалена, а баланс пересчитан.'
                    : 'Операция будет удалена без влияния на уже подтверждённый баланс.'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => { haptic('close'); closeDeleteConfirm(); }}
                className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Закрыть подтверждение удаления"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-4 rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3 dark:border-gray-800 dark:bg-gray-800/70">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {getTransactionTitle(activeTx)}
              </div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {formatCurrency(activeTx.amount)} · {activeTx.tag || 'Без категории'}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => { haptic('close'); closeDeleteConfirm(); }}
                className="rounded-xl border border-gray-200 px-3 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={async () => {
                  await handleDelete(activeTx.id);
                  setShowDeleteConfirm(false);
                }}
                className="rounded-xl bg-red-600 px-3 py-3 text-sm font-medium text-white hover:bg-red-700"
              >
                Да, удалить
              </button>
            </div>
        </ModalShell>
      )}
      <ExpensePhotoViewer transaction={photoViewerTx} onClose={() => setPhotoViewerTx(null)} />
          </div>
  );
};

export default Expenses;
